import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import CopilotWidget from '../components/CopilotWidget';
import { apiClient } from '../services/api';
import {
  queueHarvestInspection,
  loadHarvestInspectionOverlays,
} from '../services/offlineFieldAgent';
import {
  replaceReferenceDataset,
  loadReferenceDataset,
} from '../offline/referenceData';
import { subscribeSyncState } from '../offline/syncLeader';
import { MappingEvidenceRecorder } from '../services/mappingEvidence';
import { useAuth } from '../contexts/AuthContext';
import { preflightCriticalSession } from '../offline/storageSafety';
import { registerCriticalSession, updateCriticalSession } from '../offline/criticalSessions';
import {
  backendUsable,
  getConnectivitySnapshot,
  probeConnectivity,
  subscribeConnectivity,
} from '../offline/connectivity';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import PolygonCaptureMap, { PolygonCaptureHandle, PolygonCaptureStats, PolygonEvidenceObservation } from '../components/PolygonCaptureMap';
import {
  BrandMark, Card, SectionHeader, MetricTile, SmartStatus, StatusPill,
  Spinner, EmptyState, Alert, IconButton, SegmentedTabs, Avatar,
  Pagination, usePagination, Sheet,
} from '../components/ui';

// ─── Inline enterprise icon set (geometric, no cartoony outlines) ───
const Icon = {
  Users: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 11-8 0 4 4 0 018 0zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>),
  Package: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>),
  MapPin: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Search: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>),
  Plus: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 5v14M5 12h14" /></svg>),
  UserPlus: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 11a4 4 0 100-8 4 4 0 000 8zM20 8v6M23 11h-6" /></svg>),
  X: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 6L6 18M6 6l12 12" /></svg>),
  BookOpen: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>),
  Leaf: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19.5 2c-.5 4.5-.5 7.5-3 12-2 3.5-7 4-7 4zM2 21c0-3 1.85-5.36 5.08-6" /></svg>),
  Phone: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 6L9 17l-5-5" /></svg>),
  Navigation: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 11l19-9-9 19-2-8-8-2z" /></svg>),
  More: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></svg>),
  Shield: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
  // v3.11.3: Activity icon for advisory timeline button
  Activity: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>),
  // v1.6.0: Bell icon for harvest alerts
  Bell: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" /></svg>),
  // v1.7.6: Key + Dollar icons for the enterprise registration form sections
  Key: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>),
  Dollar: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>),
};

// Types
interface Farmer {
  id: string; name: string; phone: string; email?: string; village: string;
  cooperative: string | null; gps_lat: number; gps_lng: number;
  kyc_verified: boolean; consented_at: string | null; created_at: string;
  total_batches: number;
  farms_count?: number; mapped_farms?: number; eudr_verified_farms?: number;
  total_farm_area_ha?: number;
  primary_farm_id?: string;
  user_id?: string;
  // v2.1.1 fix: optional fields returned by /api/farmers list endpoint.
  available_balance?: number;
  id_number?: string;
  sustainability_tier?: string;
}

interface Batch {
  id: string; farmer_id: string; farmer_name: string; status: string;
  est_weight: number; actual_weight: number; quality_grade: string;
  price_paid: number; collected_at: string; delivered_at: string;
  moisture_content: number; fermentation_level: string; spoilage_kg: number;
  gps_lat: number; gps_lng: number; temperature: number; humidity: number;
  batch_photo_url: string; blockchain_hash: string;
}

interface Advisory {
  id: string; title: string; content: string; category: string; created_at: string;
}

type GeoPolygon = { type: string; coordinates: number[][][] };
const EMPTY_MAP_STATS: PolygonCaptureStats = { pointCount: 0, areaHectares: 0, perimeterMeters: 0, avgAccuracy: null, hasIntersection: false, mode: 'manual' };

function polygonCentroid(polygon: GeoPolygon | null): { lat: number; lng: number } | null {
  const ring = polygon?.coordinates?.[0];
  if (!ring || ring.length < 3) return null;
  const open = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring.slice(0, -1) : ring;
  if (!open.length) return null;
  const total = open.reduce((acc, c) => ({ lng: acc.lng + Number(c[0]), lat: acc.lat + Number(c[1]) }), { lng: 0, lat: 0 });
  return { lat: total.lat / open.length, lng: total.lng / open.length };
}

const FieldAgentDashboard: React.FC = () => {
  const { user } = useAuth();

  // v2.1.1 fix: include 'harvest-alerts' in the union — it was rendered but never
  // declared, causing TS2367 comparison errors at activeTab === 'harvest-alerts'.
  const [activeTab, setActiveTab] = useState<'farmers' | 'batches' | 'advisory' | 'register' | 'float' | 'inputs' | 'harvest-alerts' | 'ai-tools'>('farmers'); // v2.5
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [advisory, setAdvisory] = useState<Advisory[]>([]);
  // v1.6.0: harvest notifications from farmers
  const [harvestAlerts, setHarvestAlerts] = useState<any[]>([]);

  const [gpsLocation, setGpsLocation] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);

  const polygonRef = useRef<PolygonCaptureHandle>(null);
  const [capturedPolygon, setCapturedPolygon] = useState<GeoPolygon | null>(null);
  const [polygonArea, setPolygonArea] = useState(0);
  const [registrationMapStats, setRegistrationMapStats] = useState<PolygonCaptureStats>(EMPTY_MAP_STATS);
  const [registrationMapUpdatedAt, setRegistrationMapUpdatedAt] = useState<number | null>(null);

  const [showRegister, setShowRegister] = useState(false);
  const [showRegistrationMap, setShowRegistrationMap] = useState(false);
  // v1.5.4: expanded registration form with ALL DB fields (migration 026)
  const [registerForm, setRegisterForm] = useState({
    name: '', phone: '', email: '', village: '', village_id: '', cooperative: '',
    gps_lat: '', gps_lng: '', id_number: '', password: '',
    // v1.5.4 KYC fields (migration 026):
    date_of_birth: '', gender: '', marital_status: '', dependents_count: '',
    education_level: '', primary_language: 'en',
    cooperative_member_id: '', joined_cooperative_at: '', certification_status: 'none',
    next_of_kin_name: '', next_of_kin_phone: '', next_of_kin_relationship: '',
    mobile_money_number: '', mobile_money_provider: 'mtn',
    bank_account_number: '', bank_name: '',
    consent_agreed: false, consent_version: 'v1.0-en', dua_version: 'v1.0-en',
    consent_signature: '',
  });
  // v1.5.4: collapsible "Additional KYC" section (default collapsed)
  const [showKycSection, setShowKycSection] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const registerSubmitLockRef = useRef(false);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);

  const closeRegisterModal = useCallback(() => {
    setShowRegister(false);
    setShowRegistrationMap(false);
    if (activeTab === 'register') setActiveTab('farmers');
  }, [activeTab]);

  // Mobile-first registration contract: the Register tab always opens the guided modal.
  useEffect(() => {
    if (activeTab === 'register') setShowRegister(true);
  }, [activeTab]);

  // Registration is always dismissible. Escape is a secondary desktop/tablet exit;
  // mobile users also receive persistent visible Close/Cancel controls.
  useEffect(() => {
    if (!showRegister || showRegistrationMap) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !registerLoading) closeRegisterModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showRegister, showRegistrationMap, registerLoading, closeRegisterModal]);
  // Agent's assigned villages (loaded from /api/villages/mine)
  // v2.1.1 fix: align with /villages/mine server response (which already returns
  // division/subdivision/latitude/longitude). Previously the type was incomplete
  // which broke property access in the village dropdown's GPS auto-fill.
  const [agentVillages, setAgentVillages] = useState<{ id: string; name: string; region: string | null; division?: string | null; subdivision?: string | null; latitude?: string | null; longitude?: string | null }[]>([]);
  // v1.7.7: ALL villages (for admin role — admin can register farmers in any village)
  const [allVillages, setAllVillages] = useState<{ id: string; name: string; region: string | null; division: string | null; latitude: string | null; longitude: string | null }[]>([]);

  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showFarmerDetail, setShowFarmerDetail] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState<Farmer | null>(null);
  // v1.5.4: expanded batch form with ALL DB fields (migration 027)
  const [batchForm, setBatchForm] = useState({
    est_weight: '', actual_weight: '', quality_grade: 'B',
    moisture_content: '', fermentation_level: 'Medium', spoilage_kg: '0',
    temperature: '', humidity: '', batch_photo_url: '',
    gps_lat: '', gps_lng: '',
    // v1.5.4 new quality fields (migration 027):
    farm_id: '', harvested_at: '', wet_weight_kg: '',
    bean_count_per_100g: '', defect_insect_kg: '0', defect_mold_kg: '0', defect_flat_kg: '0',
    collection_point: '',
  });
  // v1.5.4: farms list for the farm selector (loaded when a farmer is selected)
  const [farmerFarms, setFarmerFarms] = useState<any[]>([]);
  // v1.5.4: preview grade (from POST /batches/preview-grade)
  const [previewGrade, setPreviewGrade] = useState<any | null>(null);
  // v1.6.1: pending notification ID (for linking batch → notification after creation)
  const [pendingNotificationId, setPendingNotificationId] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSuccess, setBatchSuccess] = useState<string | null>(null);

  /*
   * User-facing connectivity state.
   *
   * Raw endpoint/network failures remain diagnostic information for the
   * console. The workspace itself presents an operational state instead
   * of transport-layer errors such as ERR_NETWORK.
   */
  const [connectivityStatus, setConnectivityStatus] =
    useState(() => getConnectivitySnapshot());

  useEffect(() => {
    setConnectivityStatus(getConnectivitySnapshot());

    const unsubscribe =
      subscribeConnectivity(next => {
        setConnectivityStatus(next);
      });

    void probeConnectivity();

    return unsubscribe;
  }, []);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser');
      return;
    }
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setGpsLocation({ lat: latitude, lng: longitude, accuracy });
        setBatchForm(prev => ({ ...prev, gps_lat: latitude.toString(), gps_lng: longitude.toString() }));
        setRegisterForm(prev => ({ ...prev, gps_lat: latitude.toString(), gps_lng: longitude.toString() }));
        setGpsLoading(false);
      },
      (error) => {
        setGpsError(`Unable to get location: ${error.message}`);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  useEffect(() => {
    if (showBatchModal) getCurrentLocation();
  }, [showBatchModal, getCurrentLocation]);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const failures: string[] = [];
      const safeGet = (url: string, fallback: any) =>
        apiClient.get(url).catch((err: any) => {
          failures.push(`${url}: ${err.response?.status || err.code || 'error'}`);
          return { data: fallback };
        });

      const [farmersRes, batchesRes, advisoryRes, villagesRes] = await Promise.all([
        safeGet('/farmers', null),
        safeGet('/batches', []),
        safeGet('/advisory', []),
        safeGet('/villages/mine', null),
      ]);
      let farmerRows = Array.isArray(farmersRes.data) ? farmersRes.data : null;
      let villageRows = Array.isArray(villagesRes.data?.villages) ? villagesRes.data.villages : null;
      if (user?.id && farmerRows) {
        try { await replaceReferenceDataset(user.id, 'assigned_farmers', farmerRows, { ttlMs: 24 * 60 * 60 * 1000 }); } catch {}
      } else if (user?.id) {
        const cached = await loadReferenceDataset<Farmer>(user.id, 'assigned_farmers', true);
        farmerRows = cached.records;
        if (cached.records.length) failures.push(`farmers: using ${cached.stale ? 'stale ' : ''}local snapshot`);
      }
      if (user?.id && villageRows) {
        try { await replaceReferenceDataset(user.id, 'assigned_villages', villageRows, { ttlMs: 72 * 60 * 60 * 1000 }); } catch {}
      } else if (user?.id) {
        const cached = await loadReferenceDataset<any>(user.id, 'assigned_villages', true);
        villageRows = cached.records;
        if (cached.records.length) failures.push(`villages: using ${cached.stale ? 'stale ' : ''}local snapshot`);
      }
      setFarmers(farmerRows || []);
      setBatches(Array.isArray(batchesRes.data) ? batchesRes.data : []);
      setAdvisory(Array.isArray(advisoryRes.data) ? advisoryRes.data : []);
      setAgentVillages(villageRows || []);

      // v1.7.7: If admin, also load ALL villages (admin can register farmers in any village)
      if (user?.role === 'admin') {
        try {
          const allVillagesRes = await apiClient.get('/villages');
          setAllVillages(Array.isArray(allVillagesRes.data) ? allVillagesRes.data : []);
        } catch {
          setAllVillages([]);
        }
      }

      if (failures.length > 0) {
        // Keep endpoint-level detail available to diagnostics/developers.
        console.warn('⚠️ Some agent endpoints failed:', failures);

        /*
         * Never expose transport internals such as ERR_NETWORK or endpoint
         * paths to field users. Re-check the governed connectivity state so
         * expected offline operation is not presented as an application error.
         */
        const connectivity = await probeConnectivity();

        if (backendUsable(connectivity.state)) {
          setError(
            'Some workspace services are temporarily unavailable. ' +
            'Available sections remain usable and saved data has been preserved.'
          );
        } else {
          setError(null);
        }
      }
    } catch (err) {
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.role, user?.id]);

  // Offline-first Harvest read path.
  //
  // Server-authorized PENDING + INSPECTED rows are persisted as one
  // user/tenant-scoped reference snapshot. When the backend is unavailable,
  // the snapshot is recovered and then overlaid with local domain state.
  const fetchHarvestAlerts = useCallback(async () => {
    if (!user?.id) return;

    const applyLocalOverlay = async (records: any[]) => {
      let overlays: any[] = [];

      try {
        overlays =
          await loadHarvestInspectionOverlays(user.id);
      } catch (overlayError) {
        console.warn(
          'Unable to load Harvest local overlays:',
          overlayError,
        );
      }

      const overlayById = new Map(
        overlays.map(row => [
          String(row.resource_id || row.id || ''),
          row,
        ]),
      );

      return records.map(record => {
        const overlay =
          overlayById.get(String(record.id));

        if (
          !overlay ||
          overlay.status !== 'INSPECTED_PENDING_SYNC'
        ) {
          return record;
        }

        /*
         * ACK means the server accepted the inspection. Until ACK the local
         * business state must remain explicitly pending synchronization.
         */
        const effectiveStatus =
          overlay.sync_status === 'SYNCED'
            ? 'INSPECTED'
            : 'INSPECTED_PENDING_SYNC';

        return {
          ...record,
          status: effectiveStatus,
          local_sync_status: overlay.sync_status,
          pending_command_id:
            overlay.pending_command_id || null,
          inspected_locally_at:
            overlay.inspected_locally_at || null,
        };
      });
    };

    try {
      /*
       * Do not individually convert failed requests into empty arrays.
       * A network failure must never erase a valid local snapshot.
       */
      const [pendingRes, inspectedRes] =
        await Promise.all([
          apiClient.get(
            '/harvest-notifications?status=PENDING',
          ),
          apiClient.get(
            '/harvest-notifications?status=INSPECTED',
          ),
        ]);

      const pending =
        pendingRes.data?.notifications || [];

      const inspected =
        inspectedRes.data?.notifications || [];

      const serverRecords = [
        ...pending,
        ...inspected,
      ];

      /*
       * Snapshot persistence is best-effort relative to rendering fresh
       * server data; a local storage problem must not hide valid online data.
       *
       * The TTL matches the current conservative eight-hour offline
       * authorization window.
       */
      try {
        await replaceReferenceDataset(
          user.id,
          'harvest_notifications',
          serverRecords,
          {
            ttlMs: 8 * 60 * 60 * 1000,
          },
        );
      } catch (snapshotError) {
        console.warn(
          'Unable to persist Harvest snapshot:',
          snapshotError,
        );
      }

      setHarvestAlerts(
        await applyLocalOverlay(serverRecords),
      );
    } catch (networkError) {
      try {
        /*
         * Do not use an expired Harvest snapshot as an authorization bypass.
         */
        const snapshot =
          await loadReferenceDataset<any>(
            user.id,
            'harvest_notifications',
            false,
          );

        setHarvestAlerts(
          await applyLocalOverlay(snapshot.records),
        );

        if (snapshot.stale) {
          console.warn(
            'Harvest snapshot is stale and was not used',
          );
        }
      } catch (snapshotError) {
        console.warn(
          'Harvest offline recovery failed:',
          snapshotError,
        );
      }
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
    fetchHarvestAlerts();
  }, [fetchData, fetchHarvestAlerts]);

  /*
   * Reactively converge Harvest presentation after a durable offline
   * command ACK. The sync coordinator may run in this tab or another
   * elected browser tab, so subscribe through the shared cross-tab
   * offline sync channel rather than polling component-local state.
   *
   * The event is invalidation only: fetchHarvestAlerts() re-reads the
   * server/reference/domain state and the existing Harvest overlay rule
   * determines the effective business status.
   */
  useEffect(() => {
    if (!user?.id) return;

    return subscribeSyncState(event => {
      if (
        event.type !== 'COMMAND_ACKNOWLEDGED' ||
        event.user_id !== user.id ||
        event.resource_type !== 'harvest_notification'
      ) {
        return;
      }

      /*
       * The temporary durable-local-commit message is only valid while
       * server acknowledgement is pending. Once the Harvest command is
       * durably ACKed, clear that stale pending-sync banner and refresh
       * the authoritative/effective Harvest state.
       */
      setBatchSuccess(null);
      void fetchHarvestAlerts();
    });
  }, [user?.id, fetchHarvestAlerts]);

  // Offline-first harvest inspection.
  // Durable UI success is reported only after the IndexedDB command commit
  // succeeds. Server acceptance happens later through the elected sync leader.
  const handleInspectNotification = async (notifId: string) => {
    try {
      if (!user?.id) {
        throw new Error('Authenticated Field Agent is required');
      }

      await queueHarvestInspection(
        user.id,
        notifId,
        'Inspected by field agent',
      );

      setHarvestAlerts(previous =>
        previous.map(notification =>
          notification.id === notifId
            ? {
                ...notification,
                status: 'INSPECTED_PENDING_SYNC',
                local_sync_status: 'PENDING_SYNC',
              }
            : notification,
        ),
      );

      setError(null);
      setBatchSuccess(
        'Harvest inspection saved securely on this device. Pending server synchronization. You can continue working offline.',
      );

      /*
       * Do not refetch while genuinely offline; retain the local success state.
       * When the API is reachable, refresh server-backed alerts normally.
       */
      const connectivity = getConnectivitySnapshot();

      if (backendUsable(connectivity.state)) {
        void fetchHarvestAlerts();
      }
    } catch (err: any) {
      setBatchSuccess(null);
      setError(
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save harvest inspection',
      );
    }
  };

  // v1.6.1: create a batch from an inspected harvest notification
  const handleCreateBatchFromNotification = async (notif: any) => {
    try {
      // Pre-fill the batch form with the notification's data
      // v2.1.1 fix: cast through `unknown` because the Farmer interface requires
      // kyc_verified/consented_at/created_at/total_batches which we don't have here.
      setSelectedFarmer({
        id: notif.farmer_id,
        name: notif.farmer_name || 'Unknown',
        phone: '',
        village: notif.village || '',
        cooperative: null,
        gps_lat: 0, gps_lng: 0,
        id_number: '',
      } as unknown as Farmer);
      setBatchForm({
        est_weight: notif.expected_weight_kg?.toString() || '',
        actual_weight: '',
        quality_grade: 'B',  // will be auto-computed by server
        moisture_content: '',
        fermentation_level: 'Medium',
        spoilage_kg: '0',
        temperature: '', humidity: '', batch_photo_url: '', gps_lat: '', gps_lng: '',
        farm_id: notif.farm_id || '',
        harvested_at: notif.harvest_date || '',
        wet_weight_kg: '',
        bean_count_per_100g: '',
        defect_insect_kg: '0', defect_mold_kg: '0', defect_flat_kg: '0',
        collection_point: notif.village || '',
      });
      setPreviewGrade(null);
      if (notif.farmer_id) loadFarmerFarms(notif.farmer_id);
      setShowBatchModal(true);
      // Store the notification ID so we can link the batch after creation
      setPendingNotificationId(notif.id);
    } catch (err: any) {
      setError('Failed to start batch creation: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRegistrationPointChange = useCallback((lat: number, lng: number, meta?: { source: 'map' | 'gps'; accuracy?: number }) => {
    setRegisterForm(prev => ({ ...prev, gps_lat: lat.toFixed(7), gps_lng: lng.toFixed(7) }));
    if (meta?.source === 'gps' && typeof meta.accuracy === 'number') setGpsLocation({ lat, lng, accuracy: meta.accuracy });
    setRegistrationMapUpdatedAt(Date.now());
  }, []);

  const handleRegistrationPolygonChange = useCallback((polygon: GeoPolygon | null, area: number) => {
    setCapturedPolygon(polygon);
    setPolygonArea(area);
    const center = polygonCentroid(polygon);
    if (center) setRegisterForm(prev => ({ ...prev, gps_lat: center.lat.toFixed(7), gps_lng: center.lng.toFixed(7) }));
    setRegistrationMapUpdatedAt(Date.now());
  }, []);

  const handleRegistrationStatsChange = useCallback((stats: PolygonCaptureStats) => {
    setRegistrationMapStats(stats);
    setRegistrationMapUpdatedAt(Date.now());
  }, []);

  const handleRegisterFarmer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (registerSubmitLockRef.current) return;
    registerSubmitLockRef.current = true;
    setRegisterLoading(true);
    setRegisterSuccess(null);
    setError(null);

    // v1.6.10: validate required fields before submission
    if (!registerForm.name || !registerForm.name.trim()) {
      setError('Farmer name is required.');
      setRegisterLoading(false);
      registerSubmitLockRef.current = false;
      return;
    }
    if (!registerForm.phone || !registerForm.phone.trim()) {
      setError('Phone number is required.');
      setRegisterLoading(false);
      registerSubmitLockRef.current = false;
      return;
    }
    if (!registerForm.village) {
      setError('Village is required.');
      setRegisterLoading(false);
      registerSubmitLockRef.current = false;
      return;
    }
    if (!registerForm.id_number || !registerForm.id_number.trim()) {
      setError('National ID number is required.');
      setRegisterLoading(false);
      registerSubmitLockRef.current = false;
      return;
    }

    // v1.5.4: enforce consent before allowing registration
    if (!registerForm.consent_agreed) {
      setError('Consent is required. The farmer must explicitly agree to the data-use terms before registration.');
      setRegisterLoading(false);
      registerSubmitLockRef.current = false;
      return;
    }

    try {
      const response = await apiClient.post('/farmers', {
        name: registerForm.name,
        phone: registerForm.phone,
        email: registerForm.email || undefined,
        password: registerForm.password || undefined,
        village: registerForm.village,
        cooperative: registerForm.cooperative || null,
        gps_lat: parseFloat(registerForm.gps_lat) || null,
        gps_lng: parseFloat(registerForm.gps_lng) || null,
        id_number: registerForm.id_number,
        // v1.5.4 KYC fields:
        date_of_birth: registerForm.date_of_birth || undefined,
        gender: registerForm.gender || undefined,
        marital_status: registerForm.marital_status || undefined,
        dependents_count: registerForm.dependents_count ? parseInt(registerForm.dependents_count) : undefined,
        education_level: registerForm.education_level || undefined,
        primary_language: registerForm.primary_language || 'en',
        cooperative_member_id: registerForm.cooperative_member_id || undefined,
        joined_cooperative_at: registerForm.joined_cooperative_at || undefined,
        certification_status: registerForm.certification_status || 'none',
        next_of_kin_name: registerForm.next_of_kin_name || undefined,
        next_of_kin_phone: registerForm.next_of_kin_phone || undefined,
        next_of_kin_relationship: registerForm.next_of_kin_relationship || undefined,
        mobile_money_number: registerForm.mobile_money_number || undefined,
        mobile_money_provider: registerForm.mobile_money_provider || undefined,
        bank_account_number: registerForm.bank_account_number || undefined,
        bank_name: registerForm.bank_name || undefined,
        consent_agreed: registerForm.consent_agreed,
        consent_version: registerForm.consent_version,
        dua_version: registerForm.dua_version,
        consent_signature: registerForm.consent_signature || undefined,
      });
      // The backend returns the default password if one wasn't provided
      const defaultPwd = response.data?.default_password;
      const loginEmail = response.data?.login_email;
      const loginPhone = response.data?.login_phone;
      setRegisterSuccess(
        defaultPwd
          ? `Farmer registered! Login: ${loginEmail || loginPhone} · password: ${defaultPwd} (the farmer will be asked to change it on first login).`
          : 'Farmer registered successfully. Continue to regulatory farm mapping next.'
      );
      setRegisterForm({
        name: '', phone: '', email: '', village: '', village_id: '', cooperative: '',
        gps_lat: '', gps_lng: '', id_number: '', password: '',
        date_of_birth: '', gender: '', marital_status: '', dependents_count: '',
        education_level: '', primary_language: 'en',
        cooperative_member_id: '', joined_cooperative_at: '', certification_status: 'none',
        next_of_kin_name: '', next_of_kin_phone: '', next_of_kin_relationship: '',
        mobile_money_number: '', mobile_money_provider: 'mtn',
        bank_account_number: '', bank_name: '',
        consent_agreed: false, consent_version: 'v1.0-en', dua_version: 'v1.0-en',
        consent_signature: '',
      });
      setTimeout(() => {
        setShowRegister(false);
        setShowRegistrationMap(false);
        polygonRef.current?.clear();
        try { localStorage.removeItem('agent_registration_polygon_autosave'); } catch {}
        setCapturedPolygon(null);
        setPolygonArea(0);
        setRegistrationMapStats(EMPTY_MAP_STATS);
        setRegistrationMapUpdatedAt(null);
        setRegisterSuccess(null);
        setActiveTab('farmers');
        const createdFarmer = response.data;
        if (createdFarmer?.id && createdFarmer?.primary_farm?.id) {
          setSelectedFarmer({ ...createdFarmer, primary_farm_id: createdFarmer.primary_farm.id });
          setShowMapModal(true);
        }
        fetchData();
      }, 4500);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(message);
      setRegisterSuccess(null);
    } finally {
      registerSubmitLockRef.current = false;
      setRegisterLoading(false);
    }
  };

  const handleRecordBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    // v1.6.10: validate batch weight before submission
    const estWeight = parseFloat(batchForm.est_weight);
    if (!estWeight || estWeight <= 0) {
      setError('Estimated weight is required and must be greater than 0.');
      return;
    }
    if (batchForm.actual_weight) {
      const actualWeight = parseFloat(batchForm.actual_weight);
      if (actualWeight <= 0) {
        setError('Actual weight must be greater than 0 if provided.');
        return;
      }
    }
    if (batchForm.moisture_content) {
      const moisture = parseFloat(batchForm.moisture_content);
      if (moisture < 0 || moisture > 100) {
        setError('Moisture content must be between 0 and 100%.');
        return;
      }
    }
    setBatchLoading(true);
    setBatchSuccess(null);
    setError(null);
    try {
      const batchData = {
        farmer_id: selectedFarmer?.id,
        // v1.5.4: send farm_id for multi-farm support
        farm_id: batchForm.farm_id || undefined,
        est_weight: parseFloat(batchForm.est_weight),
        actual_weight: parseFloat(batchForm.actual_weight) || null,
        quality_grade: batchForm.quality_grade,
        moisture_content: parseFloat(batchForm.moisture_content) || null,
        fermentation_level: batchForm.fermentation_level,
        spoilage_kg: parseFloat(batchForm.spoilage_kg) || 0,
        temperature: parseFloat(batchForm.temperature) || null,
        humidity: parseFloat(batchForm.humidity) || null,
        gps_lat: parseFloat(batchForm.gps_lat) || null,
        gps_lng: parseFloat(batchForm.gps_lng) || null,
        batch_photo_url: batchForm.batch_photo_url || null,
        // v1.5.4 new quality fields:
        harvested_at: batchForm.harvested_at || undefined,
        wet_weight_kg: parseFloat(batchForm.wet_weight_kg) || undefined,
        bean_count_per_100g: parseInt(batchForm.bean_count_per_100g) || undefined,
        defect_insect_kg: parseFloat(batchForm.defect_insect_kg) || 0,
        defect_mold_kg: parseFloat(batchForm.defect_mold_kg) || 0,
        defect_flat_kg: parseFloat(batchForm.defect_flat_kg) || 0,
        collection_point: batchForm.collection_point || undefined,
      };
      const response = await apiClient.post('/batches', batchData);
      const batchId = response.data?.id || response.data?.batch?.id;

      // v1.6.1: if this batch was created from a harvest notification, link them
      if (batchId && pendingNotificationId) {
        try {
          await apiClient.post(`/harvest-notifications/${pendingNotificationId}/convert`, { batch_id: batchId });
          setBatchSuccess('Batch recorded and linked to harvest notification!');
        } catch {
          setBatchSuccess('Batch recorded (notification linking failed — non-critical)');
        }
        setPendingNotificationId(null);
      } else {
        setBatchSuccess('Batch recorded successfully!');
      }

      setTimeout(() => {
        setShowBatchModal(false);
        setBatchSuccess(null);
        setSelectedFarmer(null);
        setBatchForm({
          est_weight: '', actual_weight: '', quality_grade: 'B',
          moisture_content: '', fermentation_level: 'Medium', spoilage_kg: '0',
          temperature: '', humidity: '', batch_photo_url: '', gps_lat: '', gps_lng: '',
          farm_id: '', harvested_at: '', wet_weight_kg: '',
          bean_count_per_100g: '', defect_insect_kg: '0', defect_mold_kg: '0', defect_flat_kg: '0',
          collection_point: '',
        });
        fetchData();
        fetchHarvestAlerts();  // v1.6.1: refresh harvest alerts too
      }, 2000);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to record batch. Please try again.';
      setError(message);
      setBatchSuccess(null);
    } finally {
      setBatchLoading(false);
    }
  };

  // v1.5.4: load a farmer's farms when they're selected (for the farm selector in the batch form)
  const loadFarmerFarms = useCallback(async (farmerId: string) => {
    try {
      const res = await apiClient.get(`/farms?farmer_id=${farmerId}`);
      setFarmerFarms(res.data?.farms || []);
    } catch {
      setFarmerFarms([]);
    }
  }, []);

  // v1.5.4: preview the computed quality grade before submitting
  const handlePreviewGrade = async () => {
    try {
      const res = await apiClient.post('/batches/preview-grade', {
        moisture_content: parseFloat(batchForm.moisture_content) || undefined,
        fermentation_level: batchForm.fermentation_level,
        spoilage_kg: parseFloat(batchForm.spoilage_kg) || 0,
        est_weight: parseFloat(batchForm.est_weight) || undefined,
        actual_weight: parseFloat(batchForm.actual_weight) || undefined,
      });
      setPreviewGrade(res.data);
    } catch (err: any) {
      setError('Grade preview failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // ─── Filtered lists (computed before early return so pagination hooks are unconditional) ───
  const filteredFarmers = farmers.filter(f =>
    !searchTerm || f.name?.toLowerCase().includes(searchTerm.toLowerCase()) || f.phone?.includes(searchTerm) || f.village?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredBatches = batches.filter((b: any) =>
    !searchTerm || b.farmer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || b.id?.includes(searchTerm)
  );
  const filteredAdvisory = advisory.filter((a: any) =>
    !searchTerm || a.title?.toLowerCase().includes(searchTerm.toLowerCase()) || a.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ─── Pagination hooks (MUST be called unconditionally — before any early return) ───
  const farmersPag = usePagination(filteredFarmers, 15);
  const batchesPag = usePagination(filteredBatches, 15);
  const advisoryPag = usePagination(filteredAdvisory, 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="terrain" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading field workspace…
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'farmers',  label: 'Farmers',  icon: <Icon.Users className="w-4 h-4" />,     badge: farmers.length || undefined },
    { id: 'batches',  label: 'Batches',  icon: <Icon.Package className="w-4 h-4" />,   badge: batches.length || undefined },
    // v1.6.0: harvest alerts tab — shows pending harvest notifications from farmers
    { id: 'harvest-alerts', label: 'Harvest Alerts', icon: <Icon.Bell className="w-4 h-4" />, badge: harvestAlerts.length || undefined },
    // v1.8.1: cross-portal tabs — cash float + input distribution
    { id: 'float',    label: 'My Cash Float', icon: <Icon.Package className="w-4 h-4" /> },
    { id: 'inputs',   label: 'Input Credit', icon: <Icon.Package className="w-4 h-4" /> },
    { id: 'advisory', label: 'Advisory', icon: <Icon.BookOpen className="w-4 h-4" />,  badge: advisory.length || undefined },
    { id: 'register', label: 'Register', icon: <Icon.UserPlus className="w-4 h-4" /> },
    // v2.5: AI Tools tab — 3 AI agents for the field agent (disease, pest, knowledge)
    { id: 'ai-tools', label: 'AI Tools', icon: <Icon.Leaf className="w-4 h-4" /> }, // v2.5
  ];

  return (
    <div className="agent-dashboard-root min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.6.10: Skip-to-content link for accessibility (WCAG 2.2 AA) */}
      <a href="#agent-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      {/* ─── Top bar ─── */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="agent-topbar-identity flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="terrain" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">Field Agent workspace</p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{user?.name} · Register farmers, record batches</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={fetchData} disabled={refreshing} className="btn-secondary text-xs hide-mobile">
              <Icon.Refresh className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing…' : 'Refresh'}</span>
            </button>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing} className="md:hidden">
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <ProfileButton accent="terrain" />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="agent-main-content" data-mobile-tab={activeTab} className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6">
        {/* ─── Operational connectivity state ─── */}
        {connectivityStatus.state === 'OFFLINE' && (
          <div className="mb-5">
            <Alert tone="info">
              <div className="w-full">
                <div className="flex items-start gap-3">
                  <div
                    className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-900/5 dark:bg-white/10"
                    aria-hidden="true"
                  >
                    <span className="h-2.5 w-2.5 rounded-full bg-warning-500" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      Offline mode
                    </p>

                    <p className="mt-1 text-xs leading-relaxed opacity-90">
                      You're working from data saved securely on this device.
                      Offline-capable work will remain on this device and
                      synchronize automatically when connection returns.
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs opacity-75">
                      <span>Local data available</span>
                      <span aria-hidden="true">•</span>
                      <span>Automatic synchronization</span>
                    </div>
                  </div>
                </div>
              </div>
            </Alert>
          </div>
        )}

        {(connectivityStatus.state === 'NETWORK_PRESENT_API_UNREACHABLE' ||
          connectivityStatus.state === 'BACKEND_UNAVAILABLE') && (
          <div className="mb-5">
            <Alert tone="warning">
              <div className="w-full">
                <p className="text-sm font-semibold">
                  Limited connectivity
                </p>
                <p className="mt-1 text-xs leading-relaxed opacity-90">
                  Internet access is present, but CoConnect services cannot
                  currently be reached. Saved offline data remains available
                  where supported and synchronization will retry automatically.
                </p>
              </div>
            </Alert>
          </div>
        )}

        {/* ─── Application errors ─── */}
        {error && <div className="mb-5"><Alert tone="danger" onClose={() => setError(null)}>{error}</Alert></div>}
        {registerSuccess && <div className="mb-5"><Alert tone="success">{registerSuccess.replace(/[✅❌⏳]/g, '').trim()}</Alert></div>}
        {batchSuccess && <div className="mb-5"><Alert tone="success">{batchSuccess.replace(/[✅❌⏳]/g, '').trim()}</Alert></div>}

        {/* ─── KPIs (v1.6.10: added harvest alerts KPI) ─── */}
        <div className="agent-kpi-grid grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <MetricTile label="Farmers"           value={farmers.length}              icon={<Icon.Users className="w-4 h-4" />}    accent="terrain" className="agent-mobile-metric" />
          <MetricTile label="Batches recorded"  value={batches.length}              icon={<Icon.Package className="w-4 h-4" />}  className="agent-mobile-metric" />
          <MetricTile label="KYC verified"      value={farmers.filter(f => f.kyc_verified).length} icon={<Icon.Check className="w-4 h-4" />} className="agent-mobile-metric" />
          <MetricTile label="Harvest Alerts"    value={harvestAlerts.length}        icon={<Icon.Bell className="w-4 h-4" />} accent="terrain" className="agent-mobile-metric" />
          <MetricTile label="Advisory items"    value={advisory.length}             icon={<Icon.BookOpen className="w-4 h-4" />} className="agent-mobile-metric" />
        </div>

        {/* Mobile command actions use only existing Agent workflows/data. */}
        {activeTab === 'farmers' && (
          <section className="agent-mobile-command md:hidden" aria-label="Field operations quick actions">
            <div className="agent-mobile-command-heading">
              <div>
                <p className="section-eyebrow">Today</p>
                <h2>Field operations</h2>
              </div>
              <span>{harvestAlerts.length > 0 ? `${harvestAlerts.length} alert${harvestAlerts.length === 1 ? '' : 's'}` : 'No harvest alerts'}</span>
            </div>
            <div className="agent-mobile-command-grid">
              <button type="button" onClick={() => setActiveTab('register')}><span><Icon.UserPlus /></span><strong>Register farmer</strong><small>Guided onboarding</small></button>
              <button type="button" onClick={() => setActiveTab('harvest-alerts')}><span><Icon.Bell /></span><strong>Harvest alerts</strong><small>{harvestAlerts.length} awaiting review</small></button>
              <button type="button" onClick={() => setActiveTab('batches')}><span><Icon.Package /></span><strong>Batches</strong><small>{batches.length} recorded</small></button>
              <button type="button" onClick={() => setActiveTab('ai-tools')}><span><Icon.Leaf /></span><strong>AI tools</strong><small>Field assistance</small></button>
            </div>
          </section>
        )}

        {/* ─── Tabs (desktop) ─── */}
        <div className="hidden md:block mb-5">
          <SegmentedTabs items={tabs} active={activeTab} onChange={(id) => setActiveTab(id as any)} />
        </div>

        {/* ─── Mobile tab strip (v1.6.10: with ARIA roles) ─── */}
        <div className="agent-legacy-mobile-tabs md:hidden mb-4 -mx-4 px-4 overflow-x-auto no-scrollbar" role="tablist" aria-label="Agent navigation">
          <div className="inline-flex gap-1.5 pb-1">
            {tabs.map(t => (
              <button
                key={t.id}
                role="tab"
                data-tab-id={t.id}
                aria-selected={activeTab === t.id}
                onClick={() => setActiveTab(t.id as any)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === t.id
                    ? 'bg-ink-900 dark:bg-ink-50 text-ink-50 dark:text-ink-900'
                    : 'bg-ink-0 dark:bg-ink-850 text-ink-600 dark:text-ink-300 border border-ink-200 dark:border-ink-800'
                }`}
              >
                {t.icon}
                <span>{t.label}</span>
                {t.badge ? <span className="text-2xs opacity-70">({t.badge})</span> : null}
              </button>
            ))}
          </div>
        </div>

        {/* ─── Search ─── */}
        {activeTab !== 'register' && (
          <div className="agent-tab-search mb-5">
            <div className="relative max-w-md">
              <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}…`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
        )}

        {/* ═══ FARMERS ═══ */}
        {activeTab === 'farmers' && (
          <Card padding="none" className="agent-tab-root agent-tab-farmers agent-directory-card overflow-hidden">
            <div className="agent-directory-header p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center gap-3">
              <span className="text-sm text-ink-500 dark:text-ink-400">{farmers.length} farmers registered</span>
              <button onClick={() => setShowRegister(true)} className="btn-primary text-xs">
                <Icon.UserPlus className="w-3.5 h-3.5" /> Register farmer
              </button>
            </div>
            {farmersPag.total === 0 ? (
              <EmptyState icon={<Icon.Users className="w-5 h-5" />} title="No farmers found" description="Try a different search, or register a new farmer." />
            ) : (
              <>
                <div className="agent-farmer-list md:hidden divide-y divide-ink-200 dark:divide-ink-800">
                  {farmersPag.paginatedItems.map((farmer) => (
                    <article key={farmer.id} className="agent-farmer-row p-4">
                      <div className="flex items-start gap-3">
                        <Avatar name={farmer.name} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{farmer.name}</h3>
                              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{farmer.village} · {farmer.phone}</p>
                            </div>
                            <div className="agent-farmer-row-tools shrink-0 flex items-center gap-1.5">
                              {farmer.kyc_verified ? <StatusPill tone="success" dot>Verified</StatusPill> : <StatusPill tone="warning" dot>Pending</StatusPill>}
                              <button type="button" className="agent-farmer-detail-trigger" onClick={() => { setSelectedFarmer(farmer); setShowFarmerDetail(true); }} aria-label={`View ${farmer.name} details`}>Details</button>
                            </div>
                          </div>
                          <div className="agent-farmer-stats grid grid-cols-3 gap-2 mt-3">
                            <div><span>Farms</span><strong>{Number(farmer.farms_count ?? 0)}</strong></div>
                            <div><span>Batches</span><strong>{farmer.total_batches || 0}</strong></div>
                            <div><span>Balance</span><strong>{farmer.available_balance ? `${Number(farmer.available_balance).toLocaleString()} XAF` : '—'}</strong></div>
                          </div>
                          <div className="agent-farmer-actions grid grid-cols-2 gap-2 mt-3">
                            <button
                              onClick={() => {
                                setSelectedFarmer(farmer);
                                setBatchForm({
                                  est_weight: '', actual_weight: '', quality_grade: 'B',
                                  moisture_content: '', fermentation_level: 'Medium', spoilage_kg: '0',
                                  temperature: '', humidity: '', batch_photo_url: '', gps_lat: '', gps_lng: '',
                                  farm_id: '', harvested_at: '', wet_weight_kg: '',
                                  bean_count_per_100g: '', defect_insect_kg: '0', defect_mold_kg: '0', defect_flat_kg: '0',
                                  collection_point: '',
                                });
                                setPreviewGrade(null);
                                loadFarmerFarms(farmer.id);
                                setShowBatchModal(true);
                              }}
                              className="btn-primary text-xs"
                            >
                              <Icon.Package className="w-3.5 h-3.5" /> Record batch
                            </button>
                            <button onClick={() => { setSelectedFarmer(farmer); setShowMapModal(true); }} className="btn-secondary text-xs">
                              <Icon.MapPin className="w-3.5 h-3.5" /> Map farm
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                <table className="data-table mobile-record-table agent-farmers-desktop-records">
                  <thead><tr><th>Farmer</th><th>Village</th><th>KYC</th><th>Farms</th><th>Batches</th><th>Balance</th><th>Action</th></tr></thead>
                  <tbody>
                    {farmersPag.paginatedItems.map((farmer) => (
                      <tr key={farmer.id}>
                        <td>
                          <div className="flex items-center gap-2.5">
                            <Avatar name={farmer.name} size="sm" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-ink-900 dark:text-ink-50 truncate">{farmer.name}</p>
                              <p className="text-2xs text-ink-500 dark:text-ink-400 nums flex items-center gap-1">
                                <Icon.Phone className="w-3 h-3" /> {farmer.phone}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="text-2xs text-ink-500 dark:text-ink-400">{farmer.village}</td>
                        <td>{farmer.kyc_verified ? <StatusPill tone="success" dot>Verified</StatusPill> : <StatusPill tone="warning" dot>Pending</StatusPill>}</td>
                        <td className="text-sm">
                          <div className="flex flex-col gap-0.5">
                            <span className="nums font-medium text-ink-900 dark:text-ink-50">{Number(farmer.farms_count ?? 0)} farm{Number(farmer.farms_count ?? 0) === 1 ? '' : 's'}</span>
                            <span className="text-2xs text-ink-500 dark:text-ink-400">
                              {Number(farmer.total_farm_area_ha ?? 0).toFixed(2)} ha total
                            </span>
                            {Number(farmer.eudr_verified_farms ?? 0) > 0 && (
                              <span className="text-2xs text-success-700 dark:text-success-500 inline-flex items-center gap-0.5">
                                <Icon.Shield className="w-3 h-3" /> {Number(farmer.eudr_verified_farms)} EUDR
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="nums">{farmer.total_batches || 0}</td>
                        {/* v1.6.10: added balance column for agent visibility */}
                        <td className="nums font-medium text-ink-900 dark:text-ink-50">{farmer.available_balance ? `${Number(farmer.available_balance).toLocaleString()} XAF` : '—'}</td>
                        <td>
                          <button
                            onClick={() => {
                              setSelectedFarmer(farmer);
                              // v1.6.1: reset all batch form fields including new quality fields
                              setBatchForm({
                                est_weight: '', actual_weight: '', quality_grade: 'B',
                                moisture_content: '', fermentation_level: 'Medium', spoilage_kg: '0',
                                temperature: '', humidity: '', batch_photo_url: '', gps_lat: '', gps_lng: '',
                                farm_id: '', harvested_at: '', wet_weight_kg: '',
                                bean_count_per_100g: '', defect_insect_kg: '0', defect_mold_kg: '0', defect_flat_kg: '0',
                                collection_point: '',
                              });
                              setPreviewGrade(null);  // v1.6.1: clear previous grade preview
                              loadFarmerFarms(farmer.id);  // v1.6.1: load farms for the selector
                              setShowBatchModal(true);
                            }}
                            className="btn-secondary text-xs"
                          >
                            <Icon.Package className="w-3.5 h-3.5" /> Record batch
                          </button>
                          <button
                            onClick={() => {
                              setSelectedFarmer(farmer);
                              setShowMapModal(true);
                            }}
                            className="btn-secondary text-xs"
                            title="Map this farmer's farm boundary"
                          >
                            <Icon.MapPin className="w-3.5 h-3.5" /> Map farm
                          </button>
                          {/* v3.11.3: EUDR verify button — backend allows field_agent role */}
                          <button
                            onClick={async () => {
                              try {
                                const res = await apiClient.post(`/eudr/verify-farm/${farmer.id}`);
                                alert(`EUDR verification triggered for ${farmer.name}. Status: ${res.data?.status || 'processing'}`);
                                fetchData();
                              } catch (err: any) {
                                alert(`EUDR verification failed: ${err.response?.data?.error || err.message}`);
                              }
                            }}
                            className="btn-secondary text-xs"
                            title="Trigger EUDR plot verification for this farmer's farms"
                          >
                            <Icon.Shield className="w-3.5 h-3.5" /> Verify EUDR
                          </button>
                          {/* v3.11.3: View advisory timeline button */}
                          <button
                            onClick={async () => {
                              try {
                                const res = await apiClient.get(`/advisory-timeline/farmer/${farmer.id}?lang=en`);
                                const summary = res.data?.summary || 'No advisory available';
                                const actionCount = res.data?.timeline?.length || 0;
                                alert(`Advisory for ${farmer.name}:\n\n${summary}\n\n${actionCount} actions scheduled for the next 7 days.`);
                              } catch (err: any) {
                                alert(`Failed to load advisory: ${err.response?.data?.error || err.message}`);
                              }
                            }}
                            className="btn-secondary text-xs"
                            title="View this farmer's AI advisory timeline"
                          >
                            <Icon.Activity className="w-3.5 h-3.5" /> Advisory
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
            {farmersPag.total > farmersPag.pageSize && (
              <div className="border-t border-ink-200 dark:border-ink-800">
                <Pagination
                  page={farmersPag.page}
                  pageSize={farmersPag.pageSize}
                  total={farmersPag.total}
                  onPageChange={farmersPag.setPage}
                  onPageSizeChange={farmersPag.setPageSize}
                  showPageSize
                />
              </div>
            )}
          </Card>
        )}

        {/* ═══ BATCHES ═══ */}
        {activeTab === 'batches' && (
          <Card padding="none" className="agent-tab-root agent-tab-batches overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <span className="text-sm text-ink-500 dark:text-ink-400">{batches.length} batches recorded</span>
            </div>
            {batchesPag.total === 0 ? (
              <EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No batches found" description="Record a batch from the Farmers tab." />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table mobile-record-table agent-batches-records">
                  <thead><tr><th>Batch</th><th>Farmer</th><th>Weight</th><th>Grade</th><th>Score</th><th>Verified</th><th>Status</th></tr></thead>
                  <tbody>
                    {batchesPag.paginatedItems.map((batch: any) => (
                      <tr key={batch.id}>
                        <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.id.slice(0, 8)}…</td>
                        <td className="text-sm text-ink-700 dark:text-ink-200">{batch.farmer_name}</td>
                        <td className="nums">{batch.est_weight || batch.actual_weight || '—'} kg</td>
                        {/* v1.6.1: show grade + score */}
                        <td><StatusPill tone={batch.quality_grade === 'A' ? 'success' : batch.quality_grade === 'B' ? 'warning' : 'danger'} dot>{batch.quality_grade || '—'}</StatusPill></td>
                        <td className="nums">{batch.quality_score ? Number(batch.quality_score).toFixed(0) : '—'}</td>
                        {/* v1.6.1: show verified badge */}
                        <td>{batch.farmer_verified ? <span className="text-success-600 dark:text-success-500 text-xs">✓ Verified</span> : <span className="text-ink-400 text-xs">Pending</span>}</td>
                        <td><SmartStatus status={batch.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {batchesPag.total > batchesPag.pageSize && (
              <div className="border-t border-ink-200 dark:border-ink-800">
                <Pagination
                  page={batchesPag.page}
                  pageSize={batchesPag.pageSize}
                  total={batchesPag.total}
                  onPageChange={batchesPag.setPage}
                  onPageSizeChange={batchesPag.setPageSize}
                  showPageSize
                />
              </div>
            )}
          </Card>
        )}

        {/* ═══ v1.6.0: HARVEST ALERTS ═══ */}
        {activeTab === 'harvest-alerts' && (
          <Card padding="none" className="agent-tab-root agent-tab-harvest-alerts overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader
                eyebrow="Phase 3 — Harvest Notifications"
                title="Harvest Alerts"
                subtitle="Farmers who have signaled their cocoa is ready for pickup"
                icon={<Icon.Bell className="w-4 h-4" />}
              />
            </div>
            {harvestAlerts.length === 0 ? (
              <div className="p-8">
                <EmptyState icon={<Icon.Bell className="w-5 h-5" />} title="No pending harvest alerts" description="When farmers submit harvest notifications, they will appear here." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table mobile-record-table agent-harvest-alert-records">
                  <thead><tr><th>Notification #</th><th>Farmer</th><th>Village</th><th>Bags</th><th>Weight (kg)</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {harvestAlerts.map((n: any) => (
                      <tr key={n.id}>
                        <td className="text-xs font-mono">{n.notification_number || n.id.slice(0, 8)}</td>
                        <td className="text-sm font-medium">{n.farmer_name || '—'}</td>
                        <td className="text-2xs">{n.village || '—'}</td>
                        <td className="nums">{n.expected_bags}</td>
                        <td className="nums">{Number(n.expected_weight_kg || 0).toLocaleString()}</td>
                        <td>
                          {n.status === 'INSPECTED_PENDING_SYNC' ? (
                            <span className="text-2xs font-medium">
                              Inspected · pending sync
                            </span>
                          ) : (
                            <SmartStatus status={n.status} />
                          )}
                        </td>
                        <td>
                          {n.status === 'PENDING' ? (
                            <button
                              onClick={() =>
                                handleInspectNotification(n.id)
                              }
                              className="btn-primary text-xs"
                            >
                              <Icon.Check className="w-3 h-3" />
                              Mark Inspected
                            </button>
                          ) : n.status === 'INSPECTED_PENDING_SYNC' ? (
                            <span className="text-2xs text-ink-500 dark:text-ink-400">
                              Awaiting server sync
                            </span>
                          ) : n.status === 'INSPECTED' ? (
                            <button
                              onClick={() =>
                                handleCreateBatchFromNotification(n)
                              }
                              className="btn-accent text-xs brand-surface-terrain"
                            >
                              <Icon.Package className="w-3 h-3" />
                              Create Batch
                            </button>
                          ) : (
                            <span className="text-2xs text-ink-400">
                              {n.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* ═══ v1.8.1: AGENT CASH FLOAT ═══ */}
        {activeTab === 'float' && <div className="agent-tab-root agent-tab-float"><AgentCashFloatTab /></div>}

        {/* ═══ v1.8.1: AGENT INPUT DISTRIBUTION ═══ */}
        {activeTab === 'inputs' && <div className="agent-tab-root agent-tab-inputs"><AgentInputCreditTab farmers={farmers} /></div>}

        {/* ═══ v2.5: AI TOOLS — 3 AI agents (Disease, Pest, Knowledge) ═══ */}
        {activeTab === 'ai-tools' && <div className="agent-tab-root agent-tab-ai-tools"><AgentAIToolsTab farmers={farmers} /></div>} {/* v2.5 */}

        {/* ═══ ADVISORY ═══ */}
        {activeTab === 'advisory' && (
          <Card padding="lg" className="agent-tab-root agent-tab-advisory">
            {advisoryPag.total === 0 ? (
              <EmptyState icon={<Icon.BookOpen className="w-5 h-5" />} title="No advisory content" description="Tips from HEFARCAM agronomists will appear here." />
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {advisoryPag.paginatedItems.map((item: any) => (
                    <div key={item.id} className="p-4 surface-inset rounded-lg">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{item.title}</h3>
                        <span className="pill-info capitalize shrink-0">{item.category}</span>
                      </div>
                      <p className="text-sm text-ink-600 dark:text-ink-300 text-pretty">{item.content}</p>
                      <p className="text-2xs text-ink-400 dark:text-ink-500 mt-2 nums">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-ink-200 dark:border-ink-800">
                  <Pagination
                    page={advisoryPag.page}
                    pageSize={advisoryPag.pageSize}
                    total={advisoryPag.total}
                    onPageChange={advisoryPag.setPage}
                  />
                </div>
              </div>
            )}
          </Card>
        )}

        {/* ═══ REGISTER ═══ */}
        {activeTab === 'register' && (
          <Card padding="lg" className="agent-tab-root agent-tab-register max-w-3xl mx-auto">
            <SectionHeader
              eyebrow="Guided onboarding"
              title="Register new farmer"
              subtitle="Registration now runs in a section-by-section guided modal so each task is clear on a phone."
              icon={<Icon.UserPlus className="w-4 h-4" />}
              className="mb-5"
            />
            <div className="rounded-xl border border-ink-200 dark:border-ink-800 p-4 bg-ink-50 dark:bg-ink-800/40">
              <p className="text-sm text-ink-600 dark:text-ink-300">Complete identity, optional farm mapping, KYC/payment details, then consent and review. Required fields gate the Next button automatically.</p>
              <button type="button" onClick={() => setShowRegister(true)} className="btn-accent w-full mt-4 py-3 brand-surface-terrain">
                <Icon.UserPlus className="w-4 h-4" /> Open guided registration
              </button>
            </div>
          </Card>
        )}
      </main>

      {/* ─── Mobile-first field navigation: high-frequency field tasks + full workspace drawer ─── */}
      <nav className="agent-mobile-bottom-nav md:hidden" aria-label="Field agent primary navigation">
        <button aria-current={activeTab === 'farmers' ? 'page' : undefined} className={`agent-mobile-bottom-tab ${activeTab === 'farmers' ? 'is-active' : ''}`} onClick={() => setActiveTab('farmers')}>
          <span className="agent-mobile-bottom-icon"><Icon.Users className="w-5 h-5" /></span><span>Farmers</span>
        </button>
        <button aria-current={activeTab === 'batches' ? 'page' : undefined} className={`agent-mobile-bottom-tab ${activeTab === 'batches' ? 'is-active' : ''}`} onClick={() => setActiveTab('batches')}>
          <span className="agent-mobile-bottom-icon"><Icon.Package className="w-5 h-5" /></span><span>Batches</span>
        </button>
        <button aria-current={activeTab === 'register' ? 'page' : undefined} className={`agent-mobile-bottom-tab agent-mobile-bottom-primary ${activeTab === 'register' ? 'is-active' : ''}`} onClick={() => setActiveTab('register')}>
          <span className="agent-mobile-bottom-icon"><Icon.UserPlus className="w-5 h-5" /></span><span>Register</span>
        </button>
        <button aria-current={activeTab === 'harvest-alerts' ? 'page' : undefined} className={`agent-mobile-bottom-tab ${activeTab === 'harvest-alerts' ? 'is-active' : ''}`} onClick={() => setActiveTab('harvest-alerts')}>
          <span className="agent-mobile-bottom-icon"><Icon.Bell className="w-5 h-5" /></span><span>Alerts</span>
        </button>
        <button
          className={`agent-mobile-bottom-tab ${['float','inputs','advisory','ai-tools'].includes(activeTab) ? 'is-active' : ''}`}
          onClick={() => document.querySelector<HTMLButtonElement>('.ed-mobile-workspace-trigger')?.click()}
          aria-label="Open all field agent tools"
          aria-current={['float','inputs','advisory','ai-tools'].includes(activeTab) ? 'page' : undefined}
        >
          <span className="agent-mobile-bottom-icon"><Icon.More className="w-5 h-5" /></span><span>More</span>
        </button>
      </nav>

      {/* ═══ MAP FARM MODAL ═══ */}
      {showMapModal && selectedFarmer && (
        <MapFarmModal
          farmer={selectedFarmer}
          polygonRef={polygonRef}
          onClose={() => { setShowMapModal(false); polygonRef.current?.clear(); }}
          onSaved={() => { setShowMapModal(false); polygonRef.current?.clear(); fetchData(); }}
        />
      )}

      {/* Mobile farmer detail uses existing list data only; no new API contract. */}
      <Sheet
        open={showFarmerDetail && !!selectedFarmer}
        onClose={() => setShowFarmerDetail(false)}
        title={selectedFarmer?.name || 'Farmer details'}
        footer={selectedFarmer ? (
          <div className="grid grid-cols-2 gap-2 agent-farmer-detail-actions">
            <button type="button" className="btn-primary" onClick={() => {
              const farmer = selectedFarmer;
              setShowFarmerDetail(false);
              setBatchForm({
                est_weight: '', actual_weight: '', quality_grade: 'B',
                moisture_content: '', fermentation_level: 'Medium', spoilage_kg: '0',
                temperature: '', humidity: '', batch_photo_url: '', gps_lat: '', gps_lng: '',
                farm_id: '', harvested_at: '', wet_weight_kg: '',
                bean_count_per_100g: '', defect_insect_kg: '0', defect_mold_kg: '0', defect_flat_kg: '0',
                collection_point: '',
              });
              setPreviewGrade(null);
              loadFarmerFarms(farmer.id);
              setShowBatchModal(true);
            }}><Icon.Package className="w-4 h-4" /> Record batch</button>
            <button type="button" className="btn-secondary" onClick={() => { setShowFarmerDetail(false); setShowMapModal(true); }}><Icon.MapPin className="w-4 h-4" /> Map farm</button>
          </div>
        ) : undefined}
      >
        {selectedFarmer && (
          <div className="agent-farmer-detail space-y-4">
            <div className="flex items-center gap-3">
              <Avatar name={selectedFarmer.name} size="md" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{selectedFarmer.name}</p>
                <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{selectedFarmer.village} · {selectedFarmer.phone}</p>
              </div>
              {selectedFarmer.kyc_verified ? <StatusPill tone="success" dot>Verified</StatusPill> : <StatusPill tone="warning" dot>Pending</StatusPill>}
            </div>
            <div className="agent-farmer-detail-grid">
              <div><span>Farms</span><strong>{Number(selectedFarmer.farms_count ?? 0)}</strong></div>
              <div><span>Mapped</span><strong>{Number(selectedFarmer.mapped_farms ?? 0)}</strong></div>
              <div><span>EUDR</span><strong>{Number(selectedFarmer.eudr_verified_farms ?? 0)}</strong></div>
              <div><span>Area</span><strong>{Number(selectedFarmer.total_farm_area_ha ?? 0).toFixed(2)} ha</strong></div>
              <div><span>Batches</span><strong>{selectedFarmer.total_batches || 0}</strong></div>
              <div><span>Balance</span><strong>{selectedFarmer.available_balance != null ? `${Number(selectedFarmer.available_balance).toLocaleString()} XAF` : '—'}</strong></div>
            </div>
            <div className="agent-farmer-detail-list">
              <div><span>Phone</span><strong>{selectedFarmer.phone || '—'}</strong></div>
              <div><span>Email</span><strong>{selectedFarmer.email || '—'}</strong></div>
              <div><span>Cooperative</span><strong>{selectedFarmer.cooperative || '—'}</strong></div>
              <div><span>National ID</span><strong className="nums">{selectedFarmer.id_number || '—'}</strong></div>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-500 mb-2">Recent batches</p>
              {batches.filter(b => b.farmer_id === selectedFarmer.id).slice(0, 3).length ? (
                <div className="agent-farmer-detail-recent">
                  {batches.filter(b => b.farmer_id === selectedFarmer.id).slice(0, 3).map(b => (
                    <div key={b.id}><span className="font-mono">{b.id.slice(0, 8)}…</span><strong>{b.est_weight || b.actual_weight || '—'} kg</strong><SmartStatus status={b.status} /></div>
                  ))}
                </div>
              ) : <p className="text-xs text-ink-500">No recorded batches for this farmer.</p>}
            </div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-500 mb-2">Harvest alerts</p>
              {harvestAlerts.filter((n: any) => n.farmer_id === selectedFarmer.id || n.farmer_name === selectedFarmer.name).slice(0, 3).length ? (
                <div className="agent-farmer-detail-recent">
                  {harvestAlerts.filter((n: any) => n.farmer_id === selectedFarmer.id || n.farmer_name === selectedFarmer.name).slice(0, 3).map((n: any) => (
                    <div key={n.id}><span>{n.notification_number || n.id.slice(0, 8)}</span><strong>{Number(n.expected_weight_kg || 0).toLocaleString()} kg</strong><SmartStatus status={n.status} /></div>
                  ))}
                </div>
              ) : <p className="text-xs text-ink-500">No current harvest alerts for this farmer.</p>}
            </div>
          </div>
        )}
      </Sheet>

      {/* ═══ BATCH MODAL ═══ */}
      {showBatchModal && selectedFarmer && (
        <>
          <div className="sheet-backdrop hidden md:block" onClick={() => setShowBatchModal(false)} />
          <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-6 pointer-events-none">
            <Card padding="lg" className="max-w-2xl w-full pointer-events-auto animate-scale-in max-h-[90vh] overflow-y-auto">
              <BatchForm
                farmer={selectedFarmer}
                form={batchForm} setForm={setBatchForm}
                loading={batchLoading} success={batchSuccess}
                onSubmit={handleRecordBatch} onClose={() => setShowBatchModal(false)}
                gpsLoading={gpsLoading} gpsError={gpsError} gpsLocation={gpsLocation}
                onGetLocation={getCurrentLocation}
                farmerFarms={farmerFarms}
                previewGrade={previewGrade}
                onPreviewGrade={handlePreviewGrade}
                formId="agent-batch-form-desktop"
              />
            </Card>
          </div>
          {/* Mobile sheet */}
          <div className="md:hidden">
            {/*
             * Transaction modal top layer.
             *
             * The Field Agent bottom navigation is fixed at z-index 61.
             * Record Batch must sit above the application shell so its
             * persistent transaction footer can never be obscured or
             * intercept-conflicted by portal navigation.
             */}
            <div
              className="sheet-backdrop"
              style={{ zIndex: 89 }}
              onClick={() => {
                if (!batchLoading) setShowBatchModal(false);
              }}
            />
            <div
              className="sheet animate-sheet-up flex flex-col overflow-hidden"
              style={{
                maxHeight: '94dvh',
                zIndex: 90,
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Record batch"
            >
              <div className="sheet-handle shrink-0" />
              <div className="px-5 pb-4 pt-2 flex shrink-0 items-center justify-between bg-ink-0 dark:bg-ink-900 z-10 border-b border-ink-200 dark:border-ink-800">
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Record batch</h3>
                <IconButton label="Close" onClick={() => setShowBatchModal(false)} size="sm">
                  <Icon.X className="w-4 h-4" />
                </IconButton>
              </div>
              <div className="px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain flex-1 min-h-0">
                <BatchForm
                  farmer={selectedFarmer}
                  form={batchForm} setForm={setBatchForm}
                  loading={batchLoading} success={batchSuccess}
                  onSubmit={handleRecordBatch} onClose={() => setShowBatchModal(false)}
                  gpsLoading={gpsLoading} gpsError={gpsError} gpsLocation={gpsLocation}
                  onGetLocation={getCurrentLocation}
                  farmerFarms={farmerFarms}
                  previewGrade={previewGrade}
                  onPreviewGrade={handlePreviewGrade}
                  embedded
                  formId="agent-batch-form-mobile"
                />
              </div>

              {/*
               * Mobile transaction footer.
               * This is deliberately outside the scrolling form body so the
               * primary action remains continuously reachable on small screens.
               * The HTML form attribute submits the existing BatchForm and
               * therefore preserves handleRecordBatch and all validations.
               */}
              <div
                className="
                  shrink-0
                  border-t border-ink-200 dark:border-ink-800
                  bg-ink-0/98 dark:bg-ink-900/98
                  backdrop-blur
                  px-5 pt-3
                  pb-[max(1rem,env(safe-area-inset-bottom))]
                  shadow-[0_-8px_24px_rgba(0,0,0,0.12)]
                  z-30
                "
              >
                <button
                  type="submit"
                  form="agent-batch-form-mobile"
                  disabled={batchLoading}
                  className="
                    btn-accent
                    brand-surface-terrain
                    flex w-full
                    min-h-[52px]
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    text-sm
                    font-semibold
                  "
                >
                  {batchLoading ? (
                    <>
                      <Spinner size="sm" />
                      <span>Recording…</span>
                    </>
                  ) : (
                    <>
                      <Icon.Package className="w-4 h-4" />
                      <span>Record batch</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ GUIDED REGISTER MODAL ═══ */}
      {showRegister && (
        <div className="agent-register-modal fixed inset-0 flex items-end md:items-center justify-center md:p-6" style={{ zIndex: 90 }} role="dialog" aria-modal="true" aria-label="Register farmer">
          <div className="absolute inset-0 bg-ink-950/70 backdrop-blur-[2px]" onClick={() => { if (!registerLoading) closeRegisterModal(); }} />
          <div className="agent-register-panel relative w-full md:max-w-2xl bg-ink-0 dark:bg-ink-900 border border-ink-200 dark:border-ink-800 rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[94dvh] md:max-h-[90vh] overflow-y-auto overscroll-contain">
            <div className="sticky top-0 z-30 px-4 md:px-5 py-3 border-b border-ink-200 dark:border-ink-800 bg-ink-0/96 dark:bg-ink-900/96 backdrop-blur flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-2xs uppercase tracking-micro font-semibold text-success-600">Guided onboarding</p>
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Register farmer</h3>
              </div>
              <button
                type="button"
                onClick={closeRegisterModal}
                disabled={registerLoading}
                aria-label="Close farmer registration"
                className="agent-register-close inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-850 px-3 text-xs font-semibold text-ink-700 dark:text-ink-200 hover:bg-ink-100 dark:hover:bg-ink-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Icon.X className="w-4 h-4" />
                <span>Close</span>
              </button>
            </div>
            <div className="px-4 md:px-5 pb-4 md:pb-5">
              <RegisterInline
                form={registerForm} setForm={setRegisterForm}
                loading={registerLoading} success={registerSuccess}
                onSubmit={handleRegisterFarmer} onClose={closeRegisterModal}
                polygonRef={polygonRef}
                capturedPolygon={capturedPolygon}
                onPolygonChange={handleRegistrationPolygonChange}
                onMapStatsChange={handleRegistrationStatsChange}
                mapStats={registrationMapStats}
                mapUpdatedAt={registrationMapUpdatedAt}
                polygonArea={polygonArea}
                gpsLoading={gpsLoading} gpsError={gpsError} gpsLocation={gpsLocation}
                onGetLocation={getCurrentLocation}
                onMapPointChange={handleRegistrationPointChange}
                onOpenMap={() => setShowRegistrationMap(true)}
                mapWorkspaceOpen={showRegistrationMap}
                villages={user?.role === 'admin' ? allVillages : agentVillages}
                userRole={user?.role || 'field_agent'}
                embedded
              />
            </div>
          </div>
        </div>
      )}

      {/* Dedicated full-screen mapping workspace used by the guided registration flow. */}
      {showRegistrationMap && (
        <RegistrationMapModal
          polygonRef={polygonRef}
          polygonArea={polygonArea}
          capturedPolygon={capturedPolygon}
          mapStats={registrationMapStats}
          onPolygonChange={handleRegistrationPolygonChange}
          onMapStatsChange={handleRegistrationStatsChange}
          onMapPointChange={handleRegistrationPointChange}
          initialCenter={registerForm.gps_lat && registerForm.gps_lng ? [Number(registerForm.gps_lat), Number(registerForm.gps_lng)] : undefined}
          onClose={() => { setShowRegistrationMap(false); window.setTimeout(() => document.getElementById('agent-full-map-trigger')?.focus(), 0); }}
        />
      )}
          <CopilotWidget userRole="field_agent" />
</div>
  );
};

/* ─── GPS banner (shared) ─── */
const GpsBanner: React.FC<{
  loading: boolean;
  error: string | null;
  location: { lat: number; lng: number; accuracy: number } | null;
  onGetLocation: () => void;
}> = ({ loading, error, location, onGetLocation }) => (
  <Alert tone={error ? 'danger' : location ? 'success' : 'info'}>
    <div className="flex items-start justify-between gap-3 w-full">
      <div>
        <p className="font-medium mb-0.5">
          {loading ? 'Acquiring GPS location…' : error ? error : location ? 'GPS location acquired' : 'No GPS coordinates yet'}
        </p>
        {location && !error && (
          <p className="text-2xs nums opacity-90">
            {location.lat.toFixed(6)}, {location.lng.toFixed(6)} · accuracy {location.accuracy}m
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onGetLocation}
        disabled={loading}
        className="btn-secondary text-xs shrink-0"
      >
        <Icon.Navigation className="w-3.5 h-3.5" />
        <span>{loading ? 'Locating…' : 'Get location'}</span>
      </button>
    </div>
  </Alert>
);

/* ─── Batch form (shared between modal & sheet) ─── */
const BatchForm: React.FC<{
  farmer: Farmer;
  form: any; setForm: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean; success: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  gpsLoading: boolean; gpsError: string | null;
  gpsLocation: { lat: number; lng: number; accuracy: number } | null;
  onGetLocation: () => void;
  farmerFarms: any[];
  previewGrade: any | null;
  onPreviewGrade: () => void;
  embedded?: boolean;
  formId?: string;
}> = ({
  farmer,
  form,
  setForm,
  loading,
  success,
  onSubmit,
  gpsLoading,
  gpsError,
  gpsLocation,
  onGetLocation,
  farmerFarms,
  previewGrade,
  onPreviewGrade,
  embedded = false,
  formId,
}) => (
  <form
    id={formId}
    onSubmit={onSubmit}
    className="space-y-4"
  >
    <div className="p-3.5 rounded-lg surface-inset">
      <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Recording batch for</p>
      <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 mt-0.5">{farmer.name}</p>
      <p className="text-2xs text-ink-500 dark:text-ink-400">{farmer.village} · {farmer.phone}</p>
    </div>

    <GpsBanner loading={gpsLoading} error={gpsError} location={gpsLocation} onGetLocation={onGetLocation} />

    {/* v1.6.1: Farm selector (multi-farm support) */}
    {farmerFarms.length > 0 && (
      <div>
        <label htmlFor="b-farm" className="label">Farm</label>
        <select id="b-farm" value={form.farm_id}
          onChange={(e) => setForm({ ...form, farm_id: e.target.value })}
          className="input">
          <option value="">— Primary farm (default) —</option>
          {farmerFarms.map((f: any) => (
            <option key={f.id} value={f.id}>{f.name || f.village || 'Farm'} ({Number(f.polygon_area_hectares || 0).toFixed(1)} ha)</option>
          ))}
        </select>
      </div>
    )}

    <SectionHeader eyebrow="Weights" title="Batch weights" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label htmlFor="b-est" className="label">Estimated weight (kg) *</label>
        <input id="b-est" type="number" step="0.01" required value={form.est_weight}
          onChange={(e) => setForm({ ...form, est_weight: e.target.value })}
          className="input" placeholder="150.5" />
        {/* v1.9.4: Show estimated payout + cash float impact */}
        {form.est_weight && (
          <p className="text-2xs text-ink-500 mt-1">
            Est. payout: ~{(parseFloat(form.est_weight) * 2500).toLocaleString()} XAF
            <span className="text-warning-600 ml-1">(deducted from your cash float)</span>
          </p>
        )}
      </div>
      <div>
        <label htmlFor="b-act" className="label">Actual weight (kg)</label>
        <input id="b-act" type="number" step="0.01" value={form.actual_weight}
          onChange={(e) => setForm({ ...form, actual_weight: e.target.value })}
          className="input" placeholder="145.2" />
      </div>
      {/* v1.6.1: wet weight + harvest date */}
      <div>
        <label htmlFor="b-wet" className="label">Wet weight (kg)</label>
        <input id="b-wet" type="number" step="0.01" value={form.wet_weight_kg}
          onChange={(e) => setForm({ ...form, wet_weight_kg: e.target.value })}
          className="input" placeholder="e.g. 300" />
      </div>
      <div>
        <label htmlFor="b-harvest" className="label">Harvest date</label>
        <input
          id="b-harvest"
          type="date"
          value={
            form.harvested_at
              ? String(form.harvested_at).slice(0, 10)
              : ''
          }
          onChange={(e) =>
            setForm({
              ...form,
              harvested_at: e.target.value,
            })
          }
          className="input"
        />
      </div>
    </div>

    <SectionHeader eyebrow="Quality metrics" title="Cocoa quality (auto-graded by AI)" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label htmlFor="b-moisture" className="label">Moisture content (%)</label>
        <input id="b-moisture" type="number" step="0.01" value={form.moisture_content}
          onChange={(e) => setForm({ ...form, moisture_content: e.target.value })}
          className="input" placeholder="7.5 (ideal: 6.5-7.5)" />
      </div>
      <div>
        <label htmlFor="b-ferm" className="label">Fermentation level</label>
        <select id="b-ferm" value={form.fermentation_level}
          onChange={(e) => setForm({ ...form, fermentation_level: e.target.value })} className="input">
          <option value="Under">Under-fermented</option>
          <option value="Medium">Fully-fermented</option>
          <option value="High">Over-fermented</option>
        </select>
      </div>
      <div>
        <label htmlFor="b-spoil" className="label">Spoilage (kg)</label>
        <input id="b-spoil" type="number" step="0.01" value={form.spoilage_kg}
          onChange={(e) => setForm({ ...form, spoilage_kg: e.target.value })}
          className="input" placeholder="0.0" />
      </div>
      <div>
        <label htmlFor="b-bean" className="label">Bean count per 100g</label>
        <input id="b-bean" type="number" value={form.bean_count_per_100g}
          onChange={(e) => setForm({ ...form, bean_count_per_100g: e.target.value })}
          className="input" placeholder="90-110 = premium" />
      </div>
    </div>

    {/* v1.6.1: Defects section */}
    <div>
      <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-2">Defects (kg)</p>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label htmlFor="b-d-insect" className="text-2xs text-ink-500">Insect</label>
          <input id="b-d-insect" type="number" step="0.001" value={form.defect_insect_kg}
            onChange={(e) => setForm({ ...form, defect_insect_kg: e.target.value })}
            className="input text-sm" placeholder="0" />
        </div>
        <div>
          <label htmlFor="b-d-mold" className="text-2xs text-ink-500">Mold</label>
          <input id="b-d-mold" type="number" step="0.001" value={form.defect_mold_kg}
            onChange={(e) => setForm({ ...form, defect_mold_kg: e.target.value })}
            className="input text-sm" placeholder="0" />
        </div>
        <div>
          <label htmlFor="b-d-flat" className="text-2xs text-ink-500">Flat</label>
          <input id="b-d-flat" type="number" step="0.001" value={form.defect_flat_kg}
            onChange={(e) => setForm({ ...form, defect_flat_kg: e.target.value })}
            className="input text-sm" placeholder="0" />
        </div>
      </div>
    </div>

    {/* v1.6.1: Preview grade button */}
    <div className="flex gap-2">
      <button type="button" onClick={onPreviewGrade} disabled={loading}
        className="btn-secondary flex-1">
        <Icon.Search className="w-4 h-4" /> Preview Grade
      </button>
    </div>

    {/* v1.6.1: Grade preview result */}
    {previewGrade && (
      <div className="p-3 rounded-lg bg-ink-100 dark:bg-ink-800 space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-ink-500">Computed grade:</span>
          <span className={`text-lg font-bold ${previewGrade.grade === 'A' ? 'text-success-600 dark:text-success-500' : previewGrade.grade === 'B' ? 'text-warning-600 dark:text-warning-500' : 'text-danger-600 dark:text-danger-500'}`}>
            Grade {previewGrade.grade} · {previewGrade.score}/100
          </span>
        </div>
        {previewGrade.breakdown && (
          <div className="text-2xs text-ink-500 grid grid-cols-2 gap-1">
            <div>Moisture: {previewGrade.breakdown.moisture || '—'}</div>
            <div>Fermentation: {previewGrade.breakdown.fermentation || '—'}</div>
            <div>Spoilage: {previewGrade.breakdown.spoilage || '—'}</div>
            <div>Weight accuracy: {previewGrade.breakdown.weight_accuracy || '—'}</div>
          </div>
        )}
        <p className="text-2xs text-ink-400">This grade is computed automatically by the server on submission.</p>
      </div>
    )}

    <SectionHeader eyebrow="Environmental" title="Conditions at collection" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label htmlFor="b-temp" className="label">Temperature (°C)</label>
        <input id="b-temp" type="number" step="0.1" value={form.temperature}
          onChange={(e) => setForm({ ...form, temperature: e.target.value })}
          className="input" placeholder="25.5" />
      </div>
      <div>
        <label htmlFor="b-humid" className="label">Humidity (%)</label>
        <input id="b-humid" type="number" step="0.1" value={form.humidity}
          onChange={(e) => setForm({ ...form, humidity: e.target.value })}
          className="input" placeholder="65.0" />
      </div>
      <div>
        <label htmlFor="b-lat" className="label">GPS latitude</label>
        <input id="b-lat" type="number" step="0.0000001" value={form.gps_lat}
          onChange={(e) => setForm({ ...form, gps_lat: e.target.value })}
          className="input" placeholder="Auto-filled" />
      </div>
      <div>
        <label htmlFor="b-lng" className="label">GPS longitude</label>
        <input id="b-lng" type="number" step="0.0000001" value={form.gps_lng}
          onChange={(e) => setForm({ ...form, gps_lng: e.target.value })}
          className="input" placeholder="Auto-filled" />
      </div>
      <div>
        <label htmlFor="b-coll" className="label">Collection point</label>
        <input id="b-coll" type="text" value={form.collection_point}
          onChange={(e) => setForm({ ...form, collection_point: e.target.value })}
          className="input" placeholder="e.g. Village center" />
      </div>
      <div className="md:col-span-2">
        <label htmlFor="b-photo" className="label">Batch photo URL</label>
        <input id="b-photo" type="url" value={form.batch_photo_url}
          onChange={(e) => setForm({ ...form, batch_photo_url: e.target.value })}
          className="input" placeholder="https://…" />
      </div>
    </div>

    {success && <Alert tone="success">{success.replace(/[✅❌⏳]/g, '').trim()}</Alert>}

    {!embedded && (
      <button
        type="submit"
        disabled={loading}
        className="btn-accent w-full min-h-[48px] py-3 brand-surface-terrain"
      >
        {loading ? (
          <><Spinner size="sm" /> <span>Recording…</span></>
        ) : (
          <><Icon.Package className="w-4 h-4" /> <span>Record batch</span></>
        )}
      </button>
    )}
  </form>
);

/* ─── Guided farmer registration wizard ─── */
const RegisterInline: React.FC<{
  form: any; setForm: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean; success: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  polygonRef: React.RefObject<PolygonCaptureHandle>;
  capturedPolygon: GeoPolygon | null;
  onPolygonChange: (polygon: GeoPolygon | null, area: number) => void;
  onMapStatsChange: (stats: PolygonCaptureStats) => void;
  onMapPointChange: (lat: number, lng: number, meta?: { source: 'map' | 'gps'; accuracy?: number }) => void;
  mapStats: PolygonCaptureStats;
  mapUpdatedAt: number | null;
  polygonArea: number;
  gpsLoading: boolean; gpsError: string | null;
  gpsLocation: { lat: number; lng: number; accuracy: number } | null;
  onGetLocation: () => void;
  onOpenMap: () => void;
  mapWorkspaceOpen?: boolean;
  embedded?: boolean;
  villages?: { id: string; name: string; region?: string | null; division?: string | null; latitude?: string | null; longitude?: string | null }[];
  userRole?: string;
}> = ({ form, setForm, loading, success, onSubmit, onClose, polygonRef, capturedPolygon, onPolygonChange, onMapStatsChange, onMapPointChange, mapStats, mapUpdatedAt, polygonArea, gpsLoading, gpsError, gpsLocation, onGetLocation, onOpenMap, mapWorkspaceOpen = false, villages = [], userRole = 'field_agent' }) => {
  const [step, setStep] = useState(0);
  const steps = [
    { title: 'Identity', subtitle: 'Required farmer details' },
    { title: 'Farm location', subtitle: 'GPS center; regulatory boundary follows after registration' },
    { title: 'KYC', subtitle: 'Demographics and cooperative profile' },
    { title: 'Contacts & payment', subtitle: 'Optional payout and emergency details' },
    { title: 'Consent & review', subtitle: 'Confirm before registration' },
  ];

  const update = (field: string, value: any) => setForm((prev: any) => ({ ...prev, [field]: value }));
  const normalizedPhone = String(form.phone || '').replace(/\s/g, '');
  const phoneValid = /^((\+?237)?6\d{8})$/.test(normalizedPhone);
  const emailValid = !form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const idValid = String(form.id_number || '').trim().length >= 8;
  const identityValid = Boolean(String(form.name || '').trim() && phoneValid && String(form.village || '').trim() && idValid && emailValid);
  const consentValid = Boolean(form.consent_agreed);
  const canContinue = step === 0 ? identityValid : true;
  const canSubmit = identityValid && consentValid && !loading;
  const completedRequired = [form.name, form.phone, form.village, form.id_number].filter(v => String(v || '').trim()).length;
  const completionPct = Math.min(100, Math.round((completedRequired / 4) * 60 + (polygonArea > 0 ? 15 : 0) + (consentValid ? 25 : 0)));

  const next = () => {
    if (!canContinue || step >= steps.length - 1) return;
    setStep(v => v + 1);
  };
  const back = () => setStep(v => Math.max(0, v - 1));

  return (
    <form onSubmit={onSubmit} className="agent-register-wizard flex flex-col min-h-0">
      <div className="agent-register-step-header sticky top-0 z-20 bg-ink-0/95 dark:bg-ink-900/95 backdrop-blur border-b border-ink-200 dark:border-ink-800 -mx-1 px-1 pb-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="min-w-0">
            <p className="text-2xs uppercase tracking-micro font-semibold text-ink-500">Step {step + 1} of {steps.length}</p>
            <h4 className="text-base font-semibold text-ink-900 dark:text-ink-50 truncate">{steps[step].title}</h4>
            <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{steps[step].subtitle}</p>
          </div>
          <span className="text-xs font-bold nums text-ink-600 dark:text-ink-300">{completionPct}%</span>
        </div>
        <div className="grid grid-cols-5 gap-1" aria-label="Registration progress">
          {steps.map((item, index) => (
            <button key={item.title} type="button" disabled={index > step && !canContinue}
              onClick={() => index <= step && setStep(index)}
              className={`agent-register-progress-step h-1.5 rounded-full transition-colors ${index <= step ? 'bg-success-500' : 'bg-ink-200 dark:bg-ink-700'}`}
              aria-label={`Go to ${item.title}`} />
          ))}
        </div>
      </div>

      <div className="agent-register-step-body py-4 space-y-4 overflow-visible">
        {step === 0 && (
          <section className="space-y-4" aria-labelledby="register-step-identity">
            <div>
              <h5 id="register-step-identity" className="text-sm font-semibold text-ink-900 dark:text-ink-50">Farmer identity</h5>
              <p className="text-xs text-ink-500 dark:text-ink-400">Fields marked * are compulsory. Next unlocks only when they are complete and valid.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="m-name" className="label">Full name *</label>
                <input id="m-name" type="text" required autoComplete="name" value={form.name}
                  onChange={(e) => update('name', e.target.value)} className="input" placeholder="e.g. John Nkeng" />
              </div>
              <div>
                <label htmlFor="m-phone" className="label">Phone *</label>
                <input id="m-phone" type="tel" required inputMode="tel" autoComplete="tel" value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className={`input ${form.phone && !phoneValid ? 'border-warning-400' : ''}`} placeholder="+237 6XX XXX XXX" />
                {form.phone && !phoneValid && <p className="text-2xs text-warning-600 mt-1">Enter a valid Cameroon mobile number.</p>}
              </div>
              <div>
                <label htmlFor="m-village" className="label">Village *</label>
                {villages.length > 0 ? (
                  <select id="m-village" required value={form.village_id || ''}
                    onChange={(e) => {
                      const selectedVillage = villages.find(v => v.id === e.target.value);
                      setForm((prev: any) => ({ ...prev, village_id: e.target.value, village: selectedVillage?.name || '', gps_lat: selectedVillage?.latitude || prev.gps_lat, gps_lng: selectedVillage?.longitude || prev.gps_lng }));
                    }} className={`input ${!form.village ? 'border-warning-400' : ''}`}>
                    <option value="">Select village…</option>
                    {villages.map(v => <option key={v.id} value={v.id}>{v.name}{v.region ? ` · ${v.region}` : ''}{v.division ? ` (${v.division})` : ''}</option>)}
                  </select>
                ) : (
                  <input id="m-village" type="text" required value={form.village} onChange={(e) => update('village', e.target.value)} className="input border-warning-400" placeholder="Village name" />
                )}
                {villages.length === 0 && <p className="text-2xs text-warning-600 mt-1">{userRole === 'admin' ? 'No villages configured.' : 'No villages assigned to this agent.'}</p>}
              </div>
              <div>
                <label htmlFor="m-id" className="label">National ID *</label>
                <input id="m-id" type="text" required value={form.id_number} onChange={(e) => update('id_number', e.target.value)}
                  className={`input ${form.id_number && !idValid ? 'border-warning-400' : ''}`} placeholder="National ID number" />
                {form.id_number && !idValid && <p className="text-2xs text-warning-600 mt-1">Use at least 8 characters.</p>}
              </div>
              <div>
                <label htmlFor="m-email" className="label">Email (optional)</label>
                <input id="m-email" type="email" autoComplete="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  className={`input ${form.email && !emailValid ? 'border-warning-400' : ''}`} placeholder="farmer@example.com" />
                {form.email && !emailValid && <p className="text-2xs text-warning-600 mt-1">Enter a valid email address.</p>}
              </div>
              <div>
                <label htmlFor="m-coop" className="label">Cooperative (optional)</label>
                <input id="m-coop" type="text" value={form.cooperative} onChange={(e) => update('cooperative', e.target.value)} className="input" placeholder="Cooperative name" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="m-password" className="label">Farmer password (optional)</label>
                <input id="m-password" type="text" value={form.password} onChange={(e) => update('password', e.target.value)} className="input" placeholder="Leave blank for system default" />
                <p className="text-2xs text-ink-500 mt-1">If blank, the system provisions a temporary password and displays it once after registration.</p>
              </div>
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="space-y-4" aria-labelledby="register-step-location">
            <div>
              <h5 id="register-step-location" className="text-sm font-semibold text-ink-900 dark:text-ink-50">Farm location</h5>
              <p className="text-xs text-ink-500 dark:text-ink-400">Registration records only an approximate farm center. The regulatory boundary is captured immediately after registration in a dedicated evidence session so no provisional polygon can bypass raw-evidence preservation.</p>
            </div>
            <Alert tone="info">Boundary mapping is intentionally separated from registration. After the farmer record and primary farm exist, the evidence workspace will open automatically.</Alert>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label htmlFor="m-lat" className="label">Approximate latitude</label><input id="m-lat" type="number" step="0.0000001" value={form.gps_lat} onChange={(e) => update('gps_lat', e.target.value)} className="input" placeholder="Optional GPS center" /></div>
              <div><label htmlFor="m-lng" className="label">Approximate longitude</label><input id="m-lng" type="number" step="0.0000001" value={form.gps_lng} onChange={(e) => update('gps_lng', e.target.value)} className="input" placeholder="Optional GPS center" /></div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-ink-200 dark:border-ink-800 p-3">
              <div><p className="text-xs font-semibold text-ink-800 dark:text-ink-100">Device location</p><p className="text-2xs text-ink-500">Used only as a navigation center, never as regulatory geometry.</p></div>
              <button type="button" onClick={onGetLocation} disabled={gpsLoading} className="agent-map-button agent-map-button-tertiary">{gpsLoading ? 'Locating…' : 'Use GPS center'}</button>
            </div>
            {gpsLocation && <p className="text-xs text-ink-500">Current fix: {gpsLocation.lat.toFixed(6)}, {gpsLocation.lng.toFixed(6)} · ±{gpsLocation.accuracy.toFixed(0)} m</p>}
            {gpsError && <Alert tone="danger">{gpsError}</Alert>}
          </section>
        )}

        {step === 2 && (
          <section className="space-y-4" aria-labelledby="register-step-kyc">
            <div><h5 id="register-step-kyc" className="text-sm font-semibold text-ink-900 dark:text-ink-50">KYC & household profile</h5><p className="text-xs text-ink-500 dark:text-ink-400">Optional details improve service targeting and compliance records.</p></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label htmlFor="m-dob" className="label">Date of birth</label><input id="m-dob" type="date" value={form.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)} className="input" /></div>
              <div><label htmlFor="m-gender" className="label">Gender</label><select id="m-gender" value={form.gender} onChange={(e) => update('gender', e.target.value)} className="input"><option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></div>
              <div><label htmlFor="m-marital" className="label">Marital status</label><select id="m-marital" value={form.marital_status} onChange={(e) => update('marital_status', e.target.value)} className="input"><option value="">—</option><option value="single">Single</option><option value="married">Married</option><option value="divorced">Divorced</option><option value="widowed">Widowed</option></select></div>
              <div><label htmlFor="m-dep" className="label">Dependents</label><input id="m-dep" type="number" min="0" value={form.dependents_count} onChange={(e) => update('dependents_count', e.target.value)} className="input" placeholder="0" /></div>
              <div><label htmlFor="m-edu" className="label">Education level</label><select id="m-edu" value={form.education_level} onChange={(e) => update('education_level', e.target.value)} className="input"><option value="">—</option><option value="none">None</option><option value="primary">Primary</option><option value="secondary">Secondary</option><option value="tertiary">Tertiary</option><option value="vocational">Vocational</option></select></div>
              <div><label htmlFor="m-lang" className="label">Primary language</label><select id="m-lang" value={form.primary_language} onChange={(e) => update('primary_language', e.target.value)} className="input"><option value="en">English</option><option value="fr">French</option><option value="pcm">Pidgin</option><option value="mok">Oroko</option></select></div>
              <div><label htmlFor="m-coop-id" className="label">Cooperative member ID</label><input id="m-coop-id" type="text" value={form.cooperative_member_id} onChange={(e) => update('cooperative_member_id', e.target.value)} className="input" /></div>
              <div><label htmlFor="m-joined" className="label">Joined cooperative</label><input id="m-joined" type="date" value={form.joined_cooperative_at} onChange={(e) => update('joined_cooperative_at', e.target.value)} className="input" /></div>
              <div className="sm:col-span-2"><label htmlFor="m-cert" className="label">Certification status</label><select id="m-cert" value={form.certification_status} onChange={(e) => update('certification_status', e.target.value)} className="input"><option value="none">None</option><option value="organic">Organic</option><option value="fairtrade">Fairtrade</option><option value="rainforest_alliance">Rainforest Alliance</option><option value="utz">UTZ</option></select></div>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-5" aria-labelledby="register-step-support">
            <div><h5 id="register-step-support" className="text-sm font-semibold text-ink-900 dark:text-ink-50">Contacts & payment</h5><p className="text-xs text-ink-500 dark:text-ink-400">Optional, but useful for emergency contact and faster payout setup.</p></div>
            <div className="rounded-xl border border-ink-200 dark:border-ink-800 p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-micro text-ink-500">Next of kin</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label htmlFor="m-nokname" className="label">Name</label><input id="m-nokname" type="text" value={form.next_of_kin_name} onChange={(e) => update('next_of_kin_name', e.target.value)} className="input" /></div>
                <div><label htmlFor="m-nokphone" className="label">Phone</label><input id="m-nokphone" type="tel" value={form.next_of_kin_phone} onChange={(e) => update('next_of_kin_phone', e.target.value)} className="input" /></div>
                <div className="sm:col-span-2"><label htmlFor="m-nokrel" className="label">Relationship</label><select id="m-nokrel" value={form.next_of_kin_relationship} onChange={(e) => update('next_of_kin_relationship', e.target.value)} className="input"><option value="">— Select —</option><option value="spouse">Spouse</option><option value="parent">Parent</option><option value="child">Child</option><option value="sibling">Sibling</option><option value="other">Other</option></select></div>
              </div>
            </div>
            <div className="rounded-xl border border-ink-200 dark:border-ink-800 p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-micro text-ink-500">Payment setup</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label htmlFor="m-momo" className="label">Mobile money number</label><input id="m-momo" type="tel" value={form.mobile_money_number} onChange={(e) => update('mobile_money_number', e.target.value)} className="input" /></div>
                <div><label htmlFor="m-momoprov" className="label">Provider</label><select id="m-momoprov" value={form.mobile_money_provider} onChange={(e) => update('mobile_money_provider', e.target.value)} className="input"><option value="mtn">MTN MoMo</option><option value="orange">Orange Money</option><option value="cash">Cash</option></select></div>
                <div><label htmlFor="m-bankacct" className="label">Bank account</label><input id="m-bankacct" type="text" value={form.bank_account_number} onChange={(e) => update('bank_account_number', e.target.value)} className="input" /></div>
                <div><label htmlFor="m-bankname" className="label">Bank name</label><input id="m-bankname" type="text" value={form.bank_name} onChange={(e) => update('bank_name', e.target.value)} className="input" /></div>
              </div>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="space-y-4" aria-labelledby="register-step-review">
            <div><h5 id="register-step-review" className="text-sm font-semibold text-ink-900 dark:text-ink-50">Consent & final review</h5><p className="text-xs text-ink-500 dark:text-ink-400">Confirm the compulsory consent before creating the farmer account.</p></div>
            <div className="rounded-xl border border-ink-200 dark:border-ink-800 divide-y divide-ink-200 dark:divide-ink-800">
              <div className="p-3 flex justify-between gap-4"><span className="text-xs text-ink-500">Farmer</span><strong className="text-xs text-right text-ink-900 dark:text-ink-50">{form.name || '—'}</strong></div>
              <div className="p-3 flex justify-between gap-4"><span className="text-xs text-ink-500">Phone</span><strong className="text-xs text-right text-ink-900 dark:text-ink-50">{form.phone || '—'}</strong></div>
              <div className="p-3 flex justify-between gap-4"><span className="text-xs text-ink-500">Village</span><strong className="text-xs text-right text-ink-900 dark:text-ink-50">{form.village || '—'}</strong></div>
              <div className="p-3 flex justify-between gap-4"><span className="text-xs text-ink-500">Farm map</span><strong className="text-xs text-right text-ink-900 dark:text-ink-50">{polygonArea > 0 ? `${polygonArea.toFixed(2)} ha captured` : 'Map later'}</strong></div>
            </div>
            <label className={`flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer ${consentValid ? 'border-success-300 bg-success-50/50 dark:bg-success-500/5' : 'border-warning-300 bg-warning-50/50 dark:bg-warning-500/5'}`}>
              <input type="checkbox" checked={form.consent_agreed} onChange={(e) => update('consent_agreed', e.target.checked)} className="w-5 h-5 mt-0.5 rounded" required />
              <span className="min-w-0"><strong className="block text-sm text-ink-900 dark:text-ink-50">Data-use consent *</strong><span className="block text-xs text-ink-600 dark:text-ink-400 mt-1">The farmer has explicitly agreed to data processing for EUDR compliance, payments, and cooperative management, and understands their rights to access, correction, or withdrawal.</span><span className="block text-2xs text-ink-500 mt-2">Consent {form.consent_version} · DUA {form.dua_version}</span></span>
            </label>
            {success && <Alert tone="success">{success.replace(/[✅❌⏳]/g, '').trim()}</Alert>}
          </section>
        )}
      </div>

      <div className="agent-register-footer sticky bottom-0 z-20 bg-ink-0/96 dark:bg-ink-900/96 backdrop-blur border-t border-ink-200 dark:border-ink-800 pt-3 pb-[max(0.25rem,env(safe-area-inset-bottom))] mt-auto">
        <div className="flex gap-2">
          <button type="button" onClick={onClose} disabled={loading} className="btn-secondary min-h-[46px] px-3 shrink-0" aria-label="Cancel farmer registration">Cancel</button>
          {step > 0 && <button type="button" onClick={back} disabled={loading} className="btn-secondary flex-1 min-h-[46px]" aria-label="Previous registration step">Previous</button>}
          {step < steps.length - 1 ? (
            <button type="button" onClick={next} disabled={!canContinue || loading}
              className="btn-accent flex-1 min-h-[46px] brand-surface-terrain disabled:opacity-40 disabled:cursor-not-allowed">
              Next
            </button>
          ) : (
            <button type="submit" disabled={!canSubmit}
              className="btn-accent flex-1 min-h-[46px] brand-surface-terrain disabled:opacity-40 disabled:cursor-not-allowed">
              {loading ? <><Spinner size="sm" /> <span>Registering…</span></> : <><Icon.UserPlus className="w-4 h-4" /> <span>Register farmer</span></>}
            </button>
          )}
        </div>
        {step === 0 && !identityValid && <p className="text-2xs text-center text-warning-600 mt-2">Complete the compulsory identity fields to continue.</p>}
        {step === 4 && !consentValid && <p className="text-2xs text-center text-warning-600 mt-2">Consent is compulsory before registration.</p>}
      </div>
    </form>
  );
};

/* ─── Dedicated full-screen mapping workspace for guided registration ─── */
const RegistrationMapModal: React.FC<{
  polygonRef: React.RefObject<PolygonCaptureHandle>;
  polygonArea: number;
  capturedPolygon: GeoPolygon | null;
  mapStats: PolygonCaptureStats;
  onPolygonChange: (polygon: GeoPolygon | null, area: number) => void;
  onMapStatsChange: (stats: PolygonCaptureStats) => void;
  onMapPointChange: (lat: number, lng: number, meta?: { source: 'map' | 'gps'; accuracy?: number }) => void;
  initialCenter?: [number, number];
  onClose: () => void;
}> = ({ polygonRef, polygonArea, capturedPolygon, mapStats, onPolygonChange, onMapStatsChange, onMapPointChange, initialCenter, onClose }) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  const validBoundary = polygonArea > 0 && mapStats.pointCount >= 3 && !mapStats.hasIntersection;
  const workspace = (
    <div className="agent-registration-map-workspace agent-map-top-layer fixed inset-0 flex flex-col" role="dialog" aria-modal="true" aria-label="Farm boundary mapping workspace">
      <header className="agent-map-workspace-topbar">
        <button ref={closeButtonRef} type="button" className="agent-map-icon-control" onClick={onClose} aria-label="Return to farm location step"><Icon.X className="w-[18px] h-[18px]" /></button>
        <div className="min-w-0 flex-1">
          <p className="agent-map-eyebrow">REGISTRATION · FARM MAPPING</p>
          <h3 className="agent-map-workspace-title">Farm boundary</h3>
        </div>
        <span className={`agent-map-workspace-state ${mapStats.hasIntersection ? 'is-error' : validBoundary ? 'is-ready' : 'is-progress'}`}>
          {mapStats.hasIntersection ? 'BOUNDARY NEEDS REVIEW' : validBoundary ? 'BOUNDARY VALID' : mapStats.pointCount > 0 ? 'BOUNDARY INCOMPLETE' : 'NOT STARTED'}
        </span>
      </header>
      <div className="agent-map-workspace-canvas">
        <PolygonCaptureMap ref={polygonRef} className="agent-enterprise-map agent-enterprise-map-full" height="100%" active={true} initialCenter={initialCenter} initialPolygon={capturedPolygon} storageKey="agent_registration_polygon_autosave" autoLocate={true} onPolygonChange={onPolygonChange} onStatsChange={onMapStatsChange} onPointChange={onMapPointChange} />
      </div>
      <footer className="agent-map-workspace-tray">
        <div className="agent-map-review-copy">
          <span className={`agent-map-review-status ${mapStats.hasIntersection ? 'is-error' : validBoundary ? 'is-ready' : ''}`}>{mapStats.hasIntersection ? 'Boundary needs review' : validBoundary ? 'Boundary valid · ready to use' : mapStats.pointCount > 0 ? `Boundary incomplete · ${mapStats.pointCount} ${mapStats.pointCount === 1 ? 'vertex' : 'vertices'}` : 'Boundary not started'}</span>
          <div className="agent-map-footer-stats">
            <div><span>Area</span><strong>{polygonArea > 0 ? `${polygonArea.toFixed(2)} ha` : '—'}</strong></div>
            <div><span>Perimeter</span><strong>{mapStats.perimeterMeters > 0 ? (mapStats.perimeterMeters >= 1000 ? `${(mapStats.perimeterMeters / 1000).toFixed(2)} km` : `${Math.round(mapStats.perimeterMeters)} m`) : '—'}</strong></div>
            <div><span>Vertices</span><strong>{mapStats.pointCount || '—'}</strong></div>
          </div>
        </div>
        <div className="agent-map-workspace-actions">
          <button type="button" onClick={() => polygonRef.current?.clear()} className="agent-map-button agent-map-button-tertiary">Clear</button>
          <button type="button" onClick={onClose} disabled={mapStats.hasIntersection} className="agent-map-button agent-map-button-primary disabled:opacity-40 disabled:cursor-not-allowed">Use boundary</button>
        </div>
      </footer>
    </div>
  );
  return createPortal(workspace, document.body);
};

// React 18 StrictMode intentionally mounts passive effects twice in development.
// Keep farm resolution idempotent at the client boundary too, so both effect
// executions share one in-flight ensure request instead of producing duplicate POSTs.
const primaryFarmEnsureInFlight = new Map<string, Promise<any>>();
function ensurePrimaryFarmForMapping(farmer: Farmer): Promise<any> {
  const existing = primaryFarmEnsureInFlight.get(farmer.id);
  if (existing) return existing;
  const request = apiClient.post('/farms/ensure-primary', {
    farmer_id: farmer.id,
    name: `${farmer.village || farmer.name} farm`,
    primary_crop: 'cocoa',
  }).finally(() => primaryFarmEnsureInFlight.delete(farmer.id));
  primaryFarmEnsureInFlight.set(farmer.id, request);
  return request;
}

/* ─── Map Farm Modal (map a farmer's boundary after registration) ─── */
const MapFarmModal: React.FC<{
  farmer: Farmer;
  polygonRef: React.RefObject<PolygonCaptureHandle>;
  onClose: () => void;
  onSaved: () => void;
}> = ({ farmer, polygonRef, onClose, onSaved }) => {
  const { user: mappingUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [recoverableSession, setRecoverableSession] = useState<any | null>(null);
  const [recoveredPolygon, setRecoveredPolygon] = useState<any | null>(null);
  const [recoveredDraftPoints, setRecoveredDraftPoints] = useState<Array<{lat:number;lng:number;accuracy:number|null}> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [polygonArea, setPolygonArea] = useState(0);
  const [recorder, setRecorder] = useState<MappingEvidenceRecorder | null>(null);
  const [farmId, setFarmId] = useState<string | null>(farmer.primary_farm_id || null);
  const [evidenceCount, setEvidenceCount] = useState(0);
  const [pendingSyncGeometry, setPendingSyncGeometry] = useState<any | null>(null);
  const [capturePhase, setCapturePhase] = useState<'READY'|'CAPTURING'|'PAUSED'|'CAPTURED'>('READY');
  const [storageWarning, setStorageWarning] = useState<string | null>(null);


  useEffect(() => {
    if (!recorder || success) return;
    const unregister = registerCriticalSession({ id: recorder.clientSessionId, type: 'FARM_MAPPING', started_at: new Date().toISOString(), durable_checkpoint: true });
    return unregister;
  }, [recorder, success]);

  useEffect(() => {
    if (recorder) updateCriticalSession(recorder.clientSessionId, { durable_checkpoint: true });
  }, [recorder, evidenceCount, capturePhase]);

  useEffect(() => {
    let cancelled = false;
    const initialize = async () => {
      setInitializing(true); setError(null); setStorageWarning(null);
      try {
        const preflight = await preflightCriticalSession(25 * 1024 * 1024);
        if (preflight.outcome === 'BLOCKED') {
          throw new Error(`Mapping cannot start safely: ${preflight.reasons.join('; ')}`);
        }
        if (preflight.outcome === 'READY_WITH_WARNINGS') setStorageWarning(preflight.reasons.join(' · '));
        let resolvedFarmId = farmer.primary_farm_id || farmId;
        if (!resolvedFarmId) {
          const farms = await apiClient.get(`/farms/farmer/${farmer.id}`);
          const list = farms.data?.farms || [];
          resolvedFarmId = list.find((f: any) => f.is_primary)?.id || list[0]?.id;
          if (!resolvedFarmId) {
            const ensured = await ensurePrimaryFarmForMapping(farmer);
            resolvedFarmId = ensured.data.farm.id;
          }
        }
        if (!resolvedFarmId) throw new Error('Unable to resolve a farm for this farmer');
        if (!mappingUser?.id) throw new Error('Authenticated field-agent identity is required for local mapping recovery');
        const recoverable = await MappingEvidenceRecorder.findRecoverable(resolvedFarmId, mappingUser.id);
        if (recoverable && !cancelled) { setFarmId(resolvedFarmId); setRecoverableSession(recoverable); setInitializing(false); return; }
        const rec = await MappingEvidenceRecorder.create({ farmerId: farmer.id, farmId: resolvedFarmId, userId: mappingUser!.id });
        // Remote creation is attempted immediately but a known-farm capture can continue
        // locally if connectivity disappears; syncAndComplete retries this later.
        try { await rec.ensureRemote(); } catch (remoteErr) { console.warn('Mapping evidence server session deferred until sync', remoteErr); }
        if (!cancelled) { setFarmId(resolvedFarmId); setRecorder(rec); }
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Unable to initialize evidence capture');
      } finally { if (!cancelled) setInitializing(false); }
    };
    initialize();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmer.id, mappingUser?.id]);

  const resumeRecovered = async () => {
    if (!recoverableSession) return;
    setInitializing(true); setError(null);
    try {
      const rec = await MappingEvidenceRecorder.recover(recoverableSession.clientSessionId);
      const cp = await rec.latestCheckpoint();
      const draft = await rec.acceptedBoundaryDraft();
      const checkpointIsClosed = cp?.state === 'CAPTURED' && !!cp?.geometryPreview;
      setRecoveredPolygon(checkpointIsClosed ? cp.geometryPreview : null);
      setRecoveredDraftPoints(checkpointIsClosed ? null : draft.map(p => ({lat:p.lat,lng:p.lng,accuracy:p.accuracy})));
      setEvidenceCount(await rec.localEvidenceCount());
      try { await rec.ensureRemote(); } catch { /* resume remains offline-capable */ }
      setRecorder(rec); setRecoverableSession(null);
    }
    catch(e:any){setError(e.message||'Unable to recover mapping evidence');} finally{setInitializing(false);}
  };
  const abandonRecoveredAndRestart = async () => {
    if (!recoverableSession || !farmId) return;
    setInitializing(true); setError(null);
    try { const old=await MappingEvidenceRecorder.recover(recoverableSession.clientSessionId); await old.abandon('RECOVERY_USER_STARTED_REPLACEMENT'); const rec=await MappingEvidenceRecorder.create({farmerId:farmer.id,farmId,userId:mappingUser!.id}); try{await rec.ensureRemote();}catch{} setRecorder(rec); setRecoverableSession(null); setEvidenceCount(0); setRecoveredPolygon(null); setRecoveredDraftPoints(null); }
    catch(e:any){setError(e.message||'Unable to start replacement mapping session');} finally{setInitializing(false);}
  };

  const reviewRecovered = async () => {
    if (!recoverableSession) return;
    setInitializing(true); setError(null);
    try {
      const rec = await MappingEvidenceRecorder.recover(recoverableSession.clientSessionId);
      const cp = await rec.latestCheckpoint();
      const draft = await rec.acceptedBoundaryDraft();
      const checkpointIsClosed = cp?.state === 'CAPTURED' && !!cp?.geometryPreview;
      setRecoveredPolygon(checkpointIsClosed ? cp.geometryPreview : null);
      setRecoveredDraftPoints(checkpointIsClosed ? null : draft.map(p => ({lat:p.lat,lng:p.lng,accuracy:p.accuracy})));
      setEvidenceCount(await rec.localEvidenceCount());
      try { await rec.ensureRemote(); } catch { /* review remains available offline */ }
      await rec.recordAction('REVIEW', 'RECOVERY_REVIEW_OPENED', { checkpointState: cp?.state || null });
      setRecorder(rec);
      setRecoverableSession(null);
    } catch (e:any) { setError(e.message || 'Unable to review secured mapping evidence'); }
    finally { setInitializing(false); }
  };

  const recordEvidence = useCallback((obs: PolygonEvidenceObservation) => {
    if (!recorder) return;
    const nav:any=navigator;
    Promise.resolve(typeof nav.getBattery==='function' ? nav.getBattery().catch(()=>null) : null).then((battery:any) => recorder.record({ ...obs, deviceState: { online: navigator.onLine, visibility: document.visibilityState, thermalTelemetryAvailable: false }, batteryState: battery ? { level:battery.level, charging:battery.charging } : { available:false }, eventPayload: { ...(obs.eventPayload || {}), farmId, farmerId: farmer.id } }))
      .then(() => setEvidenceCount(v => v + 1))
      .catch((e) => setError(`Evidence persistence failed: ${e.message}`));
  }, [recorder, farmId, farmer.id]);

  const handleClose = async () => {
    if (recorder && !success) {
      try {
        const pointCount = polygonRef.current?.getPointCount() || 0;
        const polygon = polygonRef.current?.getPolygon();
        if (pendingSyncGeometry || evidenceCount > 0 || pointCount > 0) {
          await recorder.checkpoint({ state: 'PAUSED', acceptedVertexCount: pointCount, rawEventProgress: evidenceCount, geometryPreview: polygon || pendingSyncGeometry || null, syncProgress: { status: pendingSyncGeometry ? 'OFFLINE_PENDING' : 'WORKSPACE_CLOSED' } });
          try { await recorder.pause('MAPPER_CLOSED_MAPPING_WORKSPACE'); } catch { /* checkpoint is already durable locally */ }
        } else {
          await recorder.abandon('EMPTY_MAPPING_WORKSPACE_CLOSED');
        }
      } catch { /* local evidence remains durable */ }
    }
    onClose();
  };

  const handleAbandonCurrent = async () => {
    if (!recorder || success) return;
    if (!window.confirm('Abandon this mapping session? Captured evidence will remain in the audit trail, but this session will no longer be resumable.')) return;
    setSaving(true); setError(null);
    try {
      await recorder.abandon('MAPPER_EXPLICITLY_ABANDONED_SESSION');
      onClose();
    } catch (e:any) { setError(e.message || 'Unable to abandon mapping session'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!recorder || !pendingSyncGeometry || success) return;
    const retry = async () => {
      const connectivity = await probeConnectivity();
      if (!backendUsable(connectivity.state)) return;
      setSaving(true); setError(null);
      try {
        const result = await recorder.syncAndComplete(pendingSyncGeometry);
        setPendingSyncGeometry(null);
        setSuccess(result.autoApproved ? 'Offline evidence synchronized, server-validated and auto-approved under the active policy.' : `Offline evidence synchronized and validated. Decision: ${result.risk?.decision || 'review required'}.`);
        window.setTimeout(() => onSaved(), 1600);
      } catch (err:any) {
        setError(err.response?.data?.error || err.message || 'Automatic evidence synchronization retry failed');
      } finally { setSaving(false); }
    };
    window.addEventListener('online', retry);
    void probeConnectivity().then(s => { if (backendUsable(s.state)) void retry(); });
    return () => window.removeEventListener('online', retry);
  }, [recorder, pendingSyncGeometry, success, onSaved]);

  const handleSave = async () => {
    const polygon = polygonRef.current?.getPolygon();
    if (!recorder) { setError('Evidence session is not ready.'); return; }
    if (!polygon || polygon.coordinates[0].length < 4) { setError('Please capture at least 3 boundary points on the map first.'); return; }
    if (evidenceCount < 3) { setError('At least 3 evidence observations are required before synchronization.'); return; }
    setSaving(true); setError(null);
    try {
      await recorder.checkpoint({ state: 'CAPTURED', acceptedVertexCount: polygonRef.current?.getPointCount() || 0, rawEventProgress: evidenceCount, geometryPreview: polygon, syncProgress: { status: backendUsable(getConnectivitySnapshot().state) ? 'READY' : 'OFFLINE_PENDING' } });
      const connectivity = await probeConnectivity();
      if (!backendUsable(connectivity.state)) { setPendingSyncGeometry(polygon); throw new Error('Boundary evidence is safely stored offline. It will automatically resume synchronization when the Coconnect API is reachable again.'); }
      const result = await recorder.syncAndComplete(polygon);
      setSuccess(result.autoApproved ? 'Evidence synchronized, server-validated and auto-approved under the active policy.' : `Evidence synchronized and validated. Decision: ${result.risk?.decision || 'review required'}.`);
      window.setTimeout(() => onSaved(), 1600);
    } catch (err: any) { setError(err.response?.data?.error || err.message || 'Failed to synchronize mapping evidence'); }
    finally { setSaving(false); }
  };

  const evidenceStatus = initializing ? 'Preparing evidence session…' : recorder ? `${evidenceCount} raw observations secured locally${recorder.serverSessionId ? ' · server session linked' : ' · offline session'}` : 'Evidence session unavailable';
  const canSynchronize = polygonArea > 0 && evidenceCount >= 3 && capturePhase !== 'CAPTURING';
  const syncButton = (
    <button onClick={handleSave} disabled={saving || !!success || initializing || !recorder || !canSynchronize} className="btn-accent w-full py-3 brand-surface-terrain agent-mapping-sync-button">
      {saving ? <><Spinner size="sm" /> <span>Synchronizing evidence…</span></> : <><Icon.Shield className="w-4 h-4" /> <span>{canSynchronize ? 'Review, synchronize & validate' : capturePhase === 'CAPTURING' ? 'Finish mapping before validation' : 'Capture boundary to continue'}</span></>}
    </button>
  );

  const sessionActions = recorder && !success ? (
    <div className="mt-3 flex justify-end">
      <button type="button" onClick={handleAbandonCurrent} disabled={saving} className="text-xs font-semibold text-danger-600 dark:text-danger-400 hover:underline disabled:opacity-50">Abandon session</button>
    </div>
  ) : null;

  const mapPanel = (
    <>
      {recoverableSession && <div className="mb-4 rounded-xl border border-warning-300 bg-warning-50 dark:bg-warning-500/10 p-4"><p className="text-sm font-semibold">Unfinished farm mapping found</p><p className="text-xs mt-1 text-ink-600 dark:text-ink-300">Session {String(recoverableSession.clientSessionId).slice(0,8)}… · last secured {recoverableSession.updatedAt ? new Date(recoverableSession.updatedAt).toLocaleString() : 'locally'} · sequence {recoverableSession.sequence || 0}</p><div className="flex flex-wrap gap-2 mt-3"><button type="button" onClick={resumeRecovered} className="btn-primary text-xs">Resume mapping</button><button type="button" onClick={reviewRecovered} className="btn-secondary text-xs">Review capture</button><button type="button" onClick={abandonRecoveredAndRestart} className="btn-secondary text-xs">Abandon &amp; start new</button></div></div>}
      <p className="text-sm text-ink-500 dark:text-ink-400 mb-2 agent-mapping-evidence-note">Walk the farm perimeter. Coconnect preserves raw observations and rejected fixes separately; the visible boundary remains provisional until server validation.</p>
      <div className="mb-3 text-xs font-medium text-ink-600 dark:text-ink-300 agent-mapping-evidence-status"><Icon.Shield className="inline w-3.5 h-3.5 mr-1" />{evidenceStatus}</div>
      <div className="rounded-xl overflow-hidden border border-ink-200 dark:border-ink-800 mb-4">
        {!initializing && recorder ? <PolygonCaptureMap ref={polygonRef} className="agent-enterprise-map agent-enterprise-map-full agent-regulatory-mapping-map" height="400px" active={true} initialMode="automatic" storageKey={`mapping_${recorder.clientSessionId}`} initialPolygon={recoveredPolygon} initialDraftPoints={recoveredDraftPoints} capturePolicy={recorder.policy?.parameters || recorder.policy} onEvidenceObservation={recordEvidence} onCaptureAction={(eventType, reasonCode, payload) => { void recorder.recordAction(eventType, reasonCode, payload || {}); }} onCaptureStateChange={(state, reasonCode) => { if (state === 'PAUSED') { setCapturePhase('PAUSED'); void recorder.pause(reasonCode); } else if (state === 'CAPTURING' && reasonCode === 'WALK_CAPTURE_RESUMED') { setCapturePhase('CAPTURING'); void recorder.resume(reasonCode); } else if (state === 'CAPTURING') { setCapturePhase('CAPTURING'); void recorder.recordAction('START', reasonCode, { mode: 'automatic' }); } else if (state === 'CAPTURED') { setCapturePhase('CAPTURED'); } }} onPolygonChange={(polygon, area) => { setPolygonArea(area); if (polygon) void recorder.checkpoint({ acceptedVertexCount: polygonRef.current?.getPointCount() || 0, rawEventProgress: evidenceCount, geometryPreview: polygon, syncProgress: { status: backendUsable(getConnectivitySnapshot().state) ? 'LOCAL_CHECKPOINT' : 'OFFLINE' } }); }} /> : <div className="h-[400px] flex items-center justify-center text-sm text-ink-500">{recoverableSession ? 'Choose Resume mapping or Abandon & start new above.' : <Spinner />}</div>}
      </div>
      {polygonArea > 0 && <p className="text-xs text-success-700 dark:text-success-500 font-medium nums mb-3">Provisional area: {polygonArea.toFixed(2)} hectares · server calculation remains authoritative.</p>}
      {storageWarning && <div className="mb-3"><Alert tone="warning">Local storage warning: {storageWarning}</Alert></div>}
      {error && <div className="mb-3"><Alert tone={error.includes('safely stored offline') ? 'info' : 'danger'}>{error}</Alert></div>}
      {success && <div className="mb-3"><Alert tone="success">{success}</Alert></div>}
    </>
  );

  return (
    <>
      <div className="sheet-backdrop hidden md:block" onClick={handleClose} />
      <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-6 pointer-events-none">
        <Card padding="lg" className="max-w-2xl w-full pointer-events-auto animate-scale-in max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-5"><div><p className="section-eyebrow">Regulatory farm mapping evidence</p><h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{farmer.name}</h3><p className="text-xs text-ink-500 dark:text-ink-400">{farmer.village || 'Unknown village'}</p></div><IconButton label="Close" onClick={handleClose} size="sm"><Icon.X className="w-4 h-4" /></IconButton></div>
          {mapPanel}
          {syncButton}
          {sessionActions}
        </Card>
      </div>
      <div className="md:hidden">
        <div className="sheet-backdrop" onClick={handleClose} />
        <div className="sheet animate-sheet-up agent-mapping-mobile-sheet" style={{ maxHeight: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="sheet-handle" />
          <div className="px-5 pb-3 pt-3 flex items-center justify-between shrink-0 bg-ink-0 dark:bg-ink-900 z-10 agent-mapping-mobile-header"><div><p className="text-[10px] uppercase tracking-[.16em] font-semibold text-ink-500">Regulatory boundary survey</p><h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{farmer.name}</h3></div><IconButton label="Close" onClick={handleClose} size="sm"><Icon.X className="w-4 h-4" /></IconButton></div>
          <div className="px-4 pb-3 overflow-y-auto flex-1 min-h-0 agent-mapping-mobile-scroll">{mapPanel}{sessionActions}</div>
          <div className="shrink-0 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-ink-200 dark:border-ink-800 bg-ink-0/95 dark:bg-ink-900/95 backdrop-blur agent-mapping-mobile-footer">{syncButton}</div>
        </div>
      </div>
    </>
  );
};

export default FieldAgentDashboard;

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Agent Cash Float Tab
 * Field agent sees their active cash float + balance + utilization
 * ═══════════════════════════════════════════════════════════════ */
const AgentCashFloatTab: React.FC = () => {
  const [floats, setFloats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/cash-floats');
      setFloats(res.data?.floats || []);
    } catch { setFloats([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  const activeFloat = floats.find(f => f.status === 'ACTIVE');

  return (
    <div className="agent-float-page space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}

      {activeFloat && (
        <Card padding="lg" className="border-2 border-info-300">
          <SectionHeader eyebrow="Active float" title={activeFloat.float_number} subtitle={`Issued ${new Date(activeFloat.advance_date).toLocaleDateString()}`} />
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><p className="text-2xs uppercase tracking-micro text-ink-500">Advance</p><p className="text-lg font-bold nums">{Number(activeFloat.advance_amount_xaf).toLocaleString()} XAF</p></div>
            <div><p className="text-2xs uppercase tracking-micro text-ink-500">Spent</p><p className="text-lg font-bold nums text-warning-600">{Number(activeFloat.total_spent_xaf).toLocaleString()} XAF</p></div>
            <div><p className="text-2xs uppercase tracking-micro text-ink-500">Balance</p><p className={`text-lg font-bold nums ${Number(activeFloat.balance_xaf) < 0 ? 'text-danger-600' : 'text-success-600'}`}>{Number(activeFloat.balance_xaf).toLocaleString()} XAF</p></div>
            <div>
              <p className="text-2xs uppercase tracking-micro text-ink-500">Utilization</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-ink-200 dark:bg-ink-700 rounded-full overflow-hidden">
                  <div className={`h-full ${Number(activeFloat.utilization_pct) > 100 ? 'bg-danger-500' : Number(activeFloat.utilization_pct) > 80 ? 'bg-warning-500' : 'bg-success-500'}`} style={{ width: `${Math.min(Number(activeFloat.utilization_pct || 0), 100)}%` }} />
                </div>
                <span className="text-sm font-bold nums">{Number(activeFloat.utilization_pct).toFixed(0)}%</span>
              </div>
            </div>
          </div>
          {Number(activeFloat.utilization_pct) > 80 && (
            <Alert tone="warning">Your cash float is {Number(activeFloat.utilization_pct).toFixed(0)}% utilized. Contact the administrator for a top-up before you run out.</Alert>
          )}
        </Card>
      )}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="History" title="Cash Float History" subtitle="All cash advances issued to you" />
        </div>
        {floats.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No cash floats" description="Ask the administrator to issue you a cash float to start purchasing." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table agent-float-records">
              <thead><tr><th>Float #</th><th>Advance</th><th>Spent</th><th>Balance</th><th>Utilization</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {floats.map(f => (
                  // v2.1.1 (BUG#3 fix): also highlight OVER (balance went negative) in red, same as SHORTAGE
                  <tr key={f.id} className={(f.status === 'SHORTAGE' || f.status === 'OVER') ? 'bg-danger-50/50 dark:bg-danger-500/5' : ''}>
                    <td className="text-xs font-mono">{f.float_number}</td>
                    <td className="nums">{Number(f.advance_amount_xaf).toLocaleString()}</td>
                    <td className="nums">{Number(f.total_spent_xaf).toLocaleString()}</td>
                    <td className={`nums font-medium ${Number(f.balance_xaf) < 0 ? 'text-danger-600' : ''}`}>{Number(f.balance_xaf).toLocaleString()}</td>
                    <td className="nums">{Number(f.utilization_pct).toFixed(0)}%</td>
                    <td><StatusPill tone={f.status === 'ACTIVE' ? 'info' : f.status === 'RECONCILED' ? 'success' : (f.status === 'SHORTAGE' || f.status === 'OVER') ? 'danger' : 'warning'} dot>{f.status}</StatusPill></td>
                    <td className="text-2xs">{new Date(f.advance_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Agent Input Credit Tab
 * Field agent distributes inputs to farmers on credit
 * ═══════════════════════════════════════════════════════════════ */
const AgentInputCreditTab: React.FC<{ farmers: any[] }> = ({ farmers }) => {
  const [distributions, setDistributions] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ farmer_id: '', input_type_id: '', quantity: '', unit_cost_xaf: '', notes: '' });

  const refresh = async () => {
    setLoading(true);
    try {
      const [dRes, tRes] = await Promise.all([
        apiClient.get('/farm-inputs/distributions').catch(() => ({ data: { distributions: [] } })),
        apiClient.get('/farm-inputs/types').catch(() => ({ data: { types: [] } })),
      ]);
      setDistributions(dRes.data?.distributions || []);
      setTypes(tRes.data?.types || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    setError(null);
    try {
      const res = await apiClient.post('/farm-inputs/distributions', {
        ...form,
        quantity: parseFloat(form.quantity),
        unit_cost_xaf: parseFloat(form.unit_cost_xaf),
      });
      setSuccess(res.data.message);
      setShowCreate(false);
      setForm({ farmer_id: '', input_type_id: '', quantity: '', unit_cost_xaf: '', notes: '' });
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to distribute'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="agent-inputs-page space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
          <SectionHeader eyebrow="Credit Ledger" title="Input Distributions" subtitle="Distribute fertilizer, fungicide, seedlings to farmers on credit" />
          <button onClick={() => setShowCreate(true)} className="btn-primary text-xs"><Icon.Plus className="w-4 h-4" /> Distribute</button>
        </div>
        {distributions.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No distributions" description="Distribute inputs to farmers — the cost will be auto-deducted from their next payout." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table agent-input-records">
              <thead><tr><th>Distribution #</th><th>Farmer</th><th>Input</th><th>Qty</th><th>Total</th><th>Balance</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {distributions.map(d => (
                  <tr key={d.id} className={d.status === 'OUTSTANDING' ? 'bg-warning-50/30 dark:bg-warning-500/5' : ''}>
                    <td className="text-xs font-mono">{d.distribution_number}</td>
                    <td className="text-sm">{d.farmer_name}</td>
                    <td className="text-sm">{d.input_name}</td>
                    <td className="nums">{d.quantity} {d.unit}</td>
                    <td className="nums">{Number(d.total_value_xaf).toLocaleString()}</td>
                    <td className={`nums font-medium ${Number(d.balance_xaf) > 0 ? 'text-warning-600' : 'text-success-600'}`}>{Number(d.balance_xaf).toLocaleString()}</td>
                    <td><StatusPill tone={d.status === 'OUTSTANDING' ? 'warning' : d.status === 'FULLY_REPAID' ? 'success' : 'info'} dot>{d.status.replace(/_/g, ' ')}</StatusPill></td>
                    <td className="text-2xs">{new Date(d.distribution_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <Sheet open={showCreate} onClose={() => setShowCreate(false)} title="Distribute Input on Credit"
          footer={<div className="flex gap-2"><button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button><button onClick={handleCreate} disabled={!form.farmer_id || !form.input_type_id || !form.quantity} className="btn-primary flex-1">Distribute</button></div>}>
          <div className="space-y-3">
            <div><label className="label">Farmer *</label><select className="input" value={form.farmer_id} onChange={e => setForm({ ...form, farmer_id: e.target.value })}><option value="">Select farmer</option>{farmers.map((f: any) => <option key={f.id} value={f.id}>{f.name} ({f.village})</option>)}</select></div>
            <div><label className="label">Input Type *</label><select className="input" value={form.input_type_id} onChange={e => { const t = types.find(x => x.id === e.target.value); setForm({ ...form, input_type_id: e.target.value, unit_cost_xaf: t ? String(t.unit_cost_xaf) : '' }); }}><option value="">Select input</option>{types.map((t: any) => <option key={t.id} value={t.id}>{t.name} ({t.category}) - {Number(t.unit_cost_xaf).toLocaleString()} XAF/{t.unit}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-3"><div><label className="label">Quantity *</label><input type="number" step="0.1" className="input" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 50" /></div><div><label className="label">Unit Cost</label><input type="number" className="input" value={form.unit_cost_xaf} onChange={e => setForm({ ...form, unit_cost_xaf: e.target.value })} /></div></div>
            {form.quantity && form.unit_cost_xaf && <Alert tone="info">Total credit: {(parseFloat(form.quantity) * parseFloat(form.unit_cost_xaf)).toLocaleString()} XAF — auto-deducted from farmer's next payout.</Alert>}
            <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
        </Sheet>
      )}
    </div>
  );
};

/* ─── v2.5: Field Agent AI Tools Tab (3 AI agents: Disease, Pest, Knowledge) ─── */
const AgentAIToolsTab: React.FC<{ farmers: Farmer[] }> = ({ farmers }) => { // v2.5
  // v2.5: Disease Diagnosis state — symptoms + farm ID
  const [diseaseInput, setDiseaseInput] = useState<{ symptoms: string; farmId: string; farmerId: string }>({ symptoms: '', farmId: '', farmerId: '' }); // v2.5
  const [diseaseResult, setDiseaseResult] = useState<any>(null); // v2.5
  const [diseaseLoading, setDiseaseLoading] = useState(false); // v2.5
  const [diseaseError, setDiseaseError] = useState<string | null>(null); // v2.5

  // v2.5: Pest Identification state
  const [pestInput, setPestInput] = useState<{ symptoms: string; farmId: string }>({ symptoms: '', farmId: '' }); // v2.5
  const [pestResult, setPestResult] = useState<any>(null); // v2.5
  const [pestLoading, setPestLoading] = useState(false); // v2.5
  const [pestError, setPestError] = useState<string | null>(null); // v2.5

  // v2.5: Knowledge Search state
  const [knowledgeQuery, setKnowledgeQuery] = useState(''); // v2.5
  const [knowledgeResult, setKnowledgeResult] = useState<any>(null); // v2.5
  const [knowledgeLoading, setKnowledgeLoading] = useState(false); // v2.5
  const [knowledgeError, setKnowledgeError] = useState<string | null>(null); // v2.5

  // v2.5: Disease Diagnosis handler → POST /api/ai-agents/DISEASE/run
  const handleDiagnose = async () => { // v2.5
    setDiseaseLoading(true); setDiseaseError(null); setDiseaseResult(null); // v2.5
    try {
      const selectedFarmer = farmers.find((f) => f.id === diseaseInput.farmerId); // v2.5
      const res = await apiClient.post('/ai-agents/DISEASE/run', { symptoms: diseaseInput.symptoms, farmId: diseaseInput.farmId || undefined, villageName: selectedFarmer?.village }); // v2.5
      setDiseaseResult(res.data); // v2.5
    } catch (err: any) { setDiseaseError(err.response?.data?.error || err.message || 'Diagnosis failed'); } // v2.5
    finally { setDiseaseLoading(false); } // v2.5
  };

  // v2.5: Pest Identification handler → POST /api/ai-agents/PEST/run
  const handleIdentifyPest = async () => { // v2.5
    setPestLoading(true); setPestError(null); setPestResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/PEST/run', { symptoms: pestInput.symptoms, farmId: pestInput.farmId || undefined }); // v2.5
      setPestResult(res.data); // v2.5
    } catch (err: any) { setPestError(err.response?.data?.error || err.message || 'Pest identification failed'); } // v2.5
    finally { setPestLoading(false); } // v2.5
  };

  // v2.5: Knowledge Search handler → GET /api/knowledge/search/hybrid?q=...
  const handleSearchKnowledge = async () => { // v2.5
    if (!knowledgeQuery.trim()) return; // v2.5
    setKnowledgeLoading(true); setKnowledgeError(null); setKnowledgeResult(null); // v2.5
    try {
      const res = await apiClient.get(`/knowledge/search/hybrid`, { params: { q: knowledgeQuery, limit: 10 } }); // v2.5
      setKnowledgeResult(res.data); // v2.5
    } catch (err: any) { setKnowledgeError(err.response?.data?.error || err.message || 'Knowledge search failed'); } // v2.5
    finally { setKnowledgeLoading(false); } // v2.5
  };

  // v2.5: helper — render confidence score as a colored pill (0-100 scale)
  const renderConfidence = (confidence: any) => { // v2.5
    if (confidence === undefined || confidence === null) return null; // v2.5
    const pct = Math.round(Number(confidence)); // v2.5
    if (isNaN(pct)) return null; // v2.5
    const tone: 'success' | 'warning' | 'danger' = pct >= 75 ? 'success' : pct >= 50 ? 'warning' : 'danger'; // v2.5
    return <StatusPill tone={tone} dot>Confidence {pct}%</StatusPill>; // v2.5
  };

  return ( // v2.5
    <div className="agent-ai-page space-y-6"> {/* v2.5 */}
      {/* v2.5: tab intro banner */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="v2.5 AI Agents" // v2.5
          title="AI Tools" // v2.5
          subtitle="Run CoVECA's AI agents in the field — diagnose disease, identify pests, and search the knowledge base. Powered by the admin-configured Model Router (Gemini / DeepSeek / Qwen / Florence-2 / GPT-4o / Claude)." // v2.5
          icon={<Icon.Leaf className="w-4 h-4" />} // v2.5
        />
      </Card>

      {/* v2.5: ═══ Disease Diagnosis ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Agent 1 · DISEASE" // v2.5
          title="Disease Diagnosis" // v2.5
          subtitle="Diagnose cocoa disease from symptoms. Select a farmer/farm for context." // v2.5
          icon={<Icon.Shield className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3"> {/* v2.5 */}
            <div> {/* v2.5 */}
              <label className="label">Farmer (optional — used for context)</label> {/* v2.5 */}
              <select className="input" value={diseaseInput.farmerId} onChange={e => setDiseaseInput({ ...diseaseInput, farmerId: e.target.value })}> {/* v2.5 */}
                <option value="">No specific farmer</option> {/* v2.5 */}
                {farmers.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.village})</option>)} {/* v2.5 */}
              </select>
            </div>
            <div> {/* v2.5 */}
              <label className="label">Farm ID (optional)</label> {/* v2.5 */}
              <input type="text" className="input" value={diseaseInput.farmId} onChange={e => setDiseaseInput({ ...diseaseInput, farmId: e.target.value })} placeholder="e.g. 123e4567-e89b-12d3…" /> {/* v2.5 */}
            </div>
          </div>
          <div> {/* v2.5 */}
            <label className="label">Symptoms *</label> {/* v2.5 */}
            <textarea className="input" rows={3} value={diseaseInput.symptoms} onChange={e => setDiseaseInput({ ...diseaseInput, symptoms: e.target.value })} placeholder="e.g. Brown spots on pods, yellowing leaves, wilting branches…" /> {/* v2.5 */}
          </div>
          <button onClick={handleDiagnose} disabled={diseaseLoading || !diseaseInput.symptoms.trim()} className="btn-primary text-sm"> {/* v2.5 */}
            {diseaseLoading ? <><Spinner size="sm" /> Diagnosing…</> : <><Icon.Shield className="w-4 h-4" /> Diagnose</>} {/* v2.5 */}
          </button>
          {diseaseError && <Alert tone="danger" onClose={() => setDiseaseError(null)}>{diseaseError}</Alert>} {/* v2.5 */}
          {diseaseResult && ( // v2.5
            <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
              <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50"> {/* v2.5 */}
                  {diseaseResult.output?.diagnosis || diseaseResult.output?.raw_text?.slice(0, 80) || 'Diagnosis result'} {/* v2.5 */}
                </h4>
                {renderConfidence(diseaseResult.output?.confidence)} {/* v2.5 */}
              </div>
              {diseaseResult.output?.severity && <p className="text-xs text-ink-500">Severity: <span className="font-medium capitalize">{String(diseaseResult.output.severity)}</span></p>} {/* v2.5 */}
              {diseaseResult.output?.treatment_plan && Array.isArray(diseaseResult.output.treatment_plan) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Treatment plan</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {diseaseResult.output.treatment_plan.map((step: string, i: number) => <li key={i}>{step}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {diseaseResult.output?.preventive_advice && Array.isArray(diseaseResult.output.preventive_advice) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Preventive advice</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {diseaseResult.output.preventive_advice.map((step: string, i: number) => <li key={i}>{step}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {diseaseResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{diseaseResult.output.reasoning}</p>} {/* v2.5 */}
              {diseaseResult.output?.raw_text && !diseaseResult.output.diagnosis && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{diseaseResult.output.raw_text}</p>} {/* v2.5 */}
              {!diseaseResult.success && diseaseResult.error && <Alert tone="danger">{diseaseResult.error}</Alert>} {/* v2.5 */}
            </div>
          )}
        </div>
      </Card>

      {/* v2.5: ═══ Pest Identification ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Agent 2 · PEST" // v2.5
          title="Pest Identification" // v2.5
          subtitle="Identify cocoa pests (mirids, capsids, stem borers) from observed damage." // v2.5
          icon={<Icon.Leaf className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div> {/* v2.5 */}
            <label className="label">Farm ID (optional)</label> {/* v2.5 */}
            <input type="text" className="input" value={pestInput.farmId} onChange={e => setPestInput({ ...pestInput, farmId: e.target.value })} placeholder="e.g. 123e4567-e89b-12d3…" /> {/* v2.5 */}
          </div>
          <div> {/* v2.5 */}
            <label className="label">Observed damage / symptoms *</label> {/* v2.5 */}
            <textarea className="input" rows={3} value={pestInput.symptoms} onChange={e => setPestInput({ ...pestInput, symptoms: e.target.value })} placeholder="e.g. Holes in pods, ant trails on trunk, damaged growing tips…" /> {/* v2.5 */}
          </div>
          <button onClick={handleIdentifyPest} disabled={pestLoading || !pestInput.symptoms.trim()} className="btn-primary text-sm"> {/* v2.5 */}
            {pestLoading ? <><Spinner size="sm" /> Identifying…</> : <><Icon.Leaf className="w-4 h-4" /> Identify Pest</>} {/* v2.5 */}
          </button>
          {pestError && <Alert tone="danger" onClose={() => setPestError(null)}>{pestError}</Alert>} {/* v2.5 */}
          {pestResult && ( // v2.5
            <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
              <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50"> {/* v2.5 */}
                  {pestResult.output?.pest_name || pestResult.output?.diagnosis || pestResult.output?.raw_text?.slice(0, 80) || 'Pest identification result'} {/* v2.5 */}
                </h4>
                {renderConfidence(pestResult.output?.confidence)} {/* v2.5 */}
              </div>
              {pestResult.output?.severity && <p className="text-xs text-ink-500">Severity: <span className="font-medium capitalize">{String(pestResult.output.severity)}</span></p>} {/* v2.5 */}
              {pestResult.output?.description && <p className="text-sm text-ink-700 dark:text-ink-300">{pestResult.output.description}</p>} {/* v2.5 */}
              {pestResult.output?.recommendations && Array.isArray(pestResult.output.recommendations) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Recommendations</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {pestResult.output.recommendations.map((rec: string, i: number) => <li key={i}>{rec}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {pestResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{pestResult.output.reasoning}</p>} {/* v2.5 */}
              {pestResult.output?.raw_text && !pestResult.output.pest_name && !pestResult.output.diagnosis && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{pestResult.output.raw_text}</p>} {/* v2.5 */}
              {!pestResult.success && pestResult.error && <Alert tone="danger">{pestResult.error}</Alert>} {/* v2.5 */}
            </div>
          )}
        </div>
      </Card>

      {/* v2.5: ═══ Knowledge Search ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Hybrid vector + keyword search" // v2.5
          title="Knowledge Search" // v2.5
          subtitle="Search the Cameroon Cocoa Digital Twin knowledge base (IRAD, CABI, MINADER documents)." // v2.5
          icon={<Icon.BookOpen className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div className="flex gap-2"> {/* v2.5 */}
            <input type="search" className="input flex-1" value={knowledgeQuery} onChange={e => setKnowledgeQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSearchKnowledge(); }} placeholder="e.g. mirids control cameroon" aria-label="Knowledge search" /> {/* v2.5 */}
            <button onClick={handleSearchKnowledge} disabled={knowledgeLoading || !knowledgeQuery.trim()} className="btn-primary text-sm whitespace-nowrap"> {/* v2.5 */}
              {knowledgeLoading ? <><Spinner size="sm" /> Searching…</> : <><Icon.BookOpen className="w-4 h-4" /> Search</>} {/* v2.5 */}
            </button>
          </div>
          {knowledgeError && <Alert tone="danger" onClose={() => setKnowledgeError(null)}>{knowledgeError}</Alert>} {/* v2.5 */}
          {knowledgeResult && ( // v2.5
            <div className="space-y-2"> {/* v2.5 */}
              <p className="text-2xs text-ink-500"> {/* v2.5 */}
                {knowledgeResult.total || 0} results for "{knowledgeResult.query}" (search_type: {knowledgeResult.search_type || 'hybrid'})
              </p>
              {knowledgeResult.results && Array.isArray(knowledgeResult.results) && knowledgeResult.results.length > 0 ? ( // v2.5
                knowledgeResult.results.map((r: any, i: number) => ( // v2.5
                  <div key={i} className="p-3 surface-inset rounded-lg space-y-1"> {/* v2.5 */}
                    <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                      <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate"> {/* v2.5 */}
                        {r.document_title || r.title || r.chunk_text?.slice(0, 60) || `Chunk ${r.chunk_id?.slice(0, 8) || i}`} {/* v2.5 */}
                      </h4>
                      {r.similarity !== undefined && r.similarity !== null && ( // v2.5
                        <StatusPill tone="info" dot>Score {Number(r.similarity).toFixed(2)}</StatusPill> // v2.5
                      )}
                    </div>
                    {r.chunk_text && <p className="text-sm text-ink-700 dark:text-ink-300 text-pretty line-clamp-4">{r.chunk_text}</p>} {/* v2.5 */}
                    {r.document_url && <a href={r.document_url} target="_blank" rel="noreferrer" className="text-2xs text-info-600 dark:text-info-400 hover:underline inline-flex items-center gap-1">Open source</a>} {/* v2.5 */}
                  </div>
                ))
              ) : ( // v2.5
                <EmptyState icon={<Icon.BookOpen className="w-5 h-5" />} title="No results" description="Try a different query or ingest documents in Admin → Knowledge." /> // v2.5
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
